/**
 * MiraIA ••Coach Platform
 * Heartbeat: 18:06:00
 */
import React, { useEffect, useState, useRef, useCallback, useMemo, Suspense } from 'react';
import './App.css';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, User as UserIcon, MessageSquare, Gamepad2, Loader2, Send, ShieldAlert, Target } from 'lucide-react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, collection, query, where, onSnapshot, orderBy, limit, updateDoc, setDoc, documentId } from 'firebase/firestore';
import { auth, db } from './firebase';

import { useLang } from './i18n';

import AuthScreen, { getDeviceFingerprint } from './components/AuthScreen';
import EmailVerificationGate from './components/EmailVerificationGate';
import TermsAcceptanceGate from './components/TermsAcceptanceGate';
import SecurityPage from './components/SecurityPage';
import { applyPublicSeoMeta, getPublicRouteConfig, LOGIN_ROUTE_META, PublicLandingPage, PublicSeoPage } from './components/public/PublicMarketingPages';
import InstallPWA from './components/InstallPWA';
import SwipeNavigator from './components/SwipeNavigator';
import GphAgentApp from './components/gph/GphAgentApp';
import ResilientUploader from './utils/ResilientUploader';
import { normalizeAnalysis } from './utils/analysisCanonical';
import { safeUiText } from './utils/textSanitizer';
import { canonicalGameId, toLegacyGameContext } from './config/gameProfiles';
import MiraLogo from './components/ui/MiraLogo';
import { useLoading } from './context/LoadingContext';

// Lazy-loaded screens (not needed on first paint)
const Pricing = React.lazy(() => import('./components/Pricing'));
const Settings = React.lazy(() => import('./components/Settings'));
const AnalysisHistory = React.lazy(() => import('./components/AnalysisHistory'));
const GuidedHome = React.lazy(() => import('./components/GuidedHome'));
const AdminPanel = React.lazy(() => import('./components/AdminPanel'));
const PartnerPanel = React.lazy(() => import('./components/PartnerPanel'));
const ProTrainingHub = React.lazy(() => import('./components/training/ProTrainingHub'));
const SensiOptimizer = React.lazy(() => import('./components/SensiOptimizer'));
const PubgSensitivityCalculator = React.lazy(() => import('./components/PubgSensitivityCalculator'));
const FreeFireSensitivityCalculator = React.lazy(() => import('./components/FreeFireSensitivityCalculator'));
const ArenaHub = React.lazy(() => import('./components/ArenaHub'));
const MiAnalysisScreen = React.lazy(() => import('./components/MiAnalysisScreen'));


const MiCoachHome = React.lazy(() => import('./components/coach/MiCoachHome'));
const ReactMarkdown = React.lazy(() => import('react-markdown'));
const OnboardingFlow = React.lazy(() => import('./components/onboarding/OnboardingFlow'));
const ProcessingScreen = React.lazy(() => import('./components/ProcessingScreen'));

// Config & Logic
import { API_URL } from './config';
import { useMi } from './context/MiContext';
const API = API_URL;
const ONBOARDING_DONE_KEY = 'gph_onboarding_completed';
const ONBOARDING_GAME_KEY = 'gph_main_game';
const COACH_JOB_STORAGE_KEY = 'coach_jobId';
const COACH_JOB_TS_STORAGE_KEY = 'coach_job_timestamp';
const COACH_JOB_UID_STORAGE_KEY = 'coach_job_uid';
const SENSI_JOB_STORAGE_KEY = 'sensi_jobId';
const SENSI_JOB_TS_STORAGE_KEY = 'sensi_job_timestamp';
const SENSI_JOB_UID_STORAGE_KEY = 'sensi_job_uid';
const TERMS_ACCEPTED_STORAGE_KEY = 'gph_terms_accepted';
const TERMS_STATUS_SYNC_TS_KEY = 'gph_terms_status_sync_ts';
const TERMS_STATUS_TTL_MS = 6 * 60 * 60 * 1000;
const JOB_REHYDRATE_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const JOB_MONITOR_STALL_WARN_MS = 3 * 60 * 1000;
const JOB_POLL_INTERVAL_MS = 4000;
const DEFAULT_FREE_ENTITLEMENTS = Object.freeze({
  isPro: false,
  proStatus: 'none',
  calculatorLifetime: false,
  freefireCalculatorLifetime: false,
  sensiFreeUsed: 0,
  sensiFreeLimit: 5,
  sensiFreeMonthKey: '',
  coachInitialPlanUsed: false
});
const MI_ANALYSIS_TYPES = new Set(['coach', 'mi_analysis']);
const isMiAnalysisRecord = (item) => MI_ANALYSIS_TYPES.has(String(item?.type || item?.analysisType || '').toLowerCase());

const buildLocalTermsContent = (t) => ({
  user_terms: {
    title: t('local_terms_user_title'),
    content: t('local_terms_user_content')
  },
  privacy_policy: {
    title: t('local_terms_privacy_title'),
    content: t('local_terms_privacy_content')
  }
});

const resolveRuntimeCanonicalGame = ({ rawMainGame = '', rawLegacyGame = '', localGame = '' } = {}) => {
  const canonicalMainGame = canonicalGameId(rawMainGame, '');
  const canonicalLegacyGame = canonicalGameId(rawLegacyGame, '');
  const canonicalLocalGame = canonicalGameId(localGame, '');
  const canonicalRemoteGame = canonicalMainGame || canonicalLegacyGame;
  const resolvedCanonicalGame = canonicalRemoteGame || canonicalLocalGame || 'pubg_mobile';

  return {
    canonicalMainGame,
    canonicalLegacyGame,
    canonicalLocalGame,
    canonicalRemoteGame,
    resolvedCanonicalGame
  };
};

const scrollGphToTop = () => {
  if (typeof window === 'undefined') return;
  const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
  const behavior = prefersReducedMotion ? 'auto' : 'smooth';
  requestAnimationFrame(() => {
    const options = { top: 0, left: 0, behavior };
    window.scrollTo(options);
    document.documentElement.scrollTo?.(options);
    document.body.scrollTo?.(options);
    document.querySelector('.swipe-navigator')?.scrollTo?.(options);
    document.querySelector('.swipe-navigator__screen')?.scrollTo?.(options);
    document.querySelector('.screen-shell')?.scrollIntoView?.({ behavior, block: 'start' });
  });
};

const cleanString = (value) => (value == null ? '' : String(value).trim());
const createDefaultFreeEntitlements = () => ({ ...DEFAULT_FREE_ENTITLEMENTS });
const normalizeEntitlements = (data = {}) => {
  const source = data && typeof data === 'object' ? data : {};
  const sensiFreeUsed = Number(source.sensiFreeUsed);
  const sensiFreeLimit = Number(source.sensiFreeLimit);
  const sensiFreeMonthKey = cleanString(source.sensiFreeMonthKey);
  const proStatus = cleanString(source.proStatus) || 'none';
  const hasActiveProStatus = ['active', 'trialing'].includes(proStatus.toLowerCase());
  const hasLifetimePro = source.proLifetime === true || source.isLifetime === true || source.proAccessType === 'lifetime' || source.pro_lifetime?.active === true || source.entitlements?.pro_lifetime?.active === true;

  return {
    ...DEFAULT_FREE_ENTITLEMENTS,
    ...source,
    isPro: source.isPro === true || hasActiveProStatus || hasLifetimePro,
    proLifetime: hasLifetimePro,
    isLifetime: source.isLifetime === true || hasLifetimePro,
    proStatus,
    calculatorLifetime: source.calculatorLifetime === true || source.calculatorLifetimeActive === true,
    freefireCalculatorLifetime: source.freefireCalculatorLifetime === true || source.freefireCalculatorLifetimeActive === true || (source.entitlements?.freefire_calculator_lifetime?.active === true),
    sensiFreeUsed: Number.isFinite(sensiFreeUsed) ? sensiFreeUsed : DEFAULT_FREE_ENTITLEMENTS.sensiFreeUsed,
    sensiFreeLimit: Number.isFinite(sensiFreeLimit) ? sensiFreeLimit : DEFAULT_FREE_ENTITLEMENTS.sensiFreeLimit,
    sensiFreeMonthKey,
    coachInitialPlanUsed: source.coachInitialPlanUsed === true
  };
};
const getTermsCacheKey = (uid = '') => (uid ? `${TERMS_ACCEPTED_STORAGE_KEY}:${uid}` : TERMS_ACCEPTED_STORAGE_KEY);
const getTermsStatusSyncTsKey = (uid = '') => (uid ? `${TERMS_STATUS_SYNC_TS_KEY}:${uid}` : TERMS_STATUS_SYNC_TS_KEY);
const readCachedTermsAccepted = (uid = '') => {
  if (uid) {
    const scoped = localStorage.getItem(getTermsCacheKey(uid));
    if (scoped === 'true') return true;
    if (scoped === 'false') return false;
    return null;
  }
  const legacy = localStorage.getItem(TERMS_ACCEPTED_STORAGE_KEY);
  if (legacy === 'true') return true;
  if (legacy === 'false') return false;
  return null;
};
const readCachedTermsStatusSyncTs = (uid = '') => {
  const key = getTermsStatusSyncTsKey(uid);
  const raw = localStorage.getItem(key);
  if (!raw) return 0;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : 0;
};
const writeCachedTermsStatusSyncTs = (uid = '', timestampMs = Date.now()) => {
  const key = getTermsStatusSyncTsKey(uid);
  localStorage.setItem(key, String(timestampMs));
};
const writeCachedTermsAccepted = (uid = '', accepted = false) => {
  const normalized = accepted ? 'true' : 'false';
  if (uid) {
    localStorage.setItem(getTermsCacheKey(uid), normalized);
    writeCachedTermsStatusSyncTs(uid);
    return;
  }
  localStorage.setItem(TERMS_ACCEPTED_STORAGE_KEY, normalized);
  writeCachedTermsStatusSyncTs('');
};

const FREE_ACCESS_CREDITS_FLOOR = 999999;
const isPrivilegedAccount = (firebaseUser, userProfile) => {
  if (!firebaseUser && !userProfile) return false;
  const role = String(userProfile?.role || '').toLowerCase();
  return firebaseUser?.claims?.admin === true || role === 'admin' || role === 'partner';
};

// Helper: Create or Update User via BACKEND (guarantees welcome credits + referral)
const initUserOnBackend = async (user, referralCode = null) => {
  try {
    const token = await user.getIdToken(true);
    const body = { deviceFingerprint: getDeviceFingerprint() };
    if (referralCode) body.referralCode = referralCode;

    const response = await fetch(`${API}/auth/init`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(body)
    });

    if (response.status === 401) {
      console.warn("[AUTH] Init failed (401) — continuing in degraded mode");
      return null;
    }

    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (e) {
    console.error("[AUTH] Backend Init Error:", e.message);
    return null;
  }
};

function App() {
  const { isLoading: isGlobalAppLoading, startLoading, stopLoading } = useLoading();

  const { lang, language, changeLanguage, t } = useLang();
  const localTermsContent = useMemo(() => buildLocalTermsContent(t), [t]);
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [analyses, setAnalyses] = useState([]);
  const [socialFeed, setSocialFeed] = useState([]);
  const [socialLeaderboards, setSocialLeaderboards] = useState({ daily: [], weekly: [], all_time: [] });
  const [currentAnalysis, setCurrentAnalysis] = useState(null);

  const refreshingRef = useRef(false);
  const lastRefreshRef = useRef(0);

  window.refreshUserEntitlements = async ({ force = false, reason = 'unknown' } = {}) => {
    if (refreshingRef.current) return null;
    const now = Date.now();
    if (!force && (now - lastRefreshRef.current < 30000)) return null;

    try {
      refreshingRef.current = true;
      const currentUser = auth.currentUser;
      if (!currentUser) return null;
      
      const token = await currentUser.getIdToken();
      const res = await fetch(`${API}/auth/init`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({})
      });
      
      if (!res.ok) throw new Error('Failed to fetch latest entitlements');
      const data = await res.json();
      
      lastRefreshRef.current = Date.now();
      
      setProfile(prev => {
        const updated = { ...prev, ...data.user, entitlements: data.entitlements };
        window.dispatchEvent(new CustomEvent("gph:entitlements-updated", {
          detail: { reason, user: data.user, entitlements: data.entitlements, profile: updated }
        }));
        return updated;
      });
      
      return data;
    } catch (err) {
      console.error('[REFRESH] Error:', err);
      return null;
    } finally {
      refreshingRef.current = false;
    }
  };

  const [errorModal, setErrorModal] = useState({ show: false, title: '', message: '', kind: '' });

  const [authInitialized, setAuthInitialized] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [gameContext, setGameContext] = useState(() => {
    const localGame = localStorage.getItem(ONBOARDING_GAME_KEY) || localStorage.getItem('selected_game') || 'pubg_mobile';
    return toLegacyGameContext(canonicalGameId(localGame, 'pubg_mobile'));
  });
  const [trainingContext, setTrainingContext] = useState(null);
  const [activeTab, _setActiveTab] = useState('home');
  const [coachResetSignal] = useState(0);
  const [initialCoachView, setInitialCoachView] = useState(null);

  const navigateToMiAnalysis = useCallback(() => {
    _setActiveTab('mi-analysis');
  }, []);

  const setActiveTab = useCallback((nextTabVal) => {
    const normalized = nextTabVal === 'hub'
      ? 'home'
      : nextTabVal === 'settings'
      ? 'profile'
      : nextTabVal;
    scrollGphToTop();
    _setActiveTab(normalized);
  }, []);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentModalTab, setPaymentModalTab] = useState('plans');
  const [paymentError, setPaymentError] = useState('');
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [showPartnerPanel, setShowPartnerPanel] = useState(false);
  const [drillContext, setDrillContext] = useState(null);
  const [selectedVideoTrimMeta, setSelectedVideoTrimMeta] = useState(null);
  const [coachLaunchContext, setCoachLaunchContext] = useState(null);

  const [isProcessing, setIsProcessing] = useState(false);
  const [coachStatus, setCoachStatus] = useState({ status: 'idle', progress: 0, subStatus: '', error: null });
  const [sensiStatus, setSensiStatus] = useState({ status: 'idle', progress: 0, subStatus: '', error: null });
  const [sensiError, setSensiError] = useState(null);
  const [sensiStall, setSensiStall] = useState(false);
  const [coachJobId, setCoachJobId] = useState(null);
  const [sensiJobId, setSensiJobId] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isChatSending, setIsChatSending] = useState(false);
  const [mi, setMi] = useState({ mood: 'neutral', message: t('app_mi_syncing'), context: t('app_mi_context_system') });

  const hasPrivilegedAccess = useMemo(
    () => isPrivilegedAccount(user, profile),
    [user, profile]
  );

  const effectiveProfile = useMemo(() => {
    if (!profile) return profile;
    if (!hasPrivilegedAccess) return profile;

    const currentCredits = Number(profile?.credits || 0);
    const guaranteedCredits = Number.isFinite(currentCredits)
      ? Math.max(currentCredits, FREE_ACCESS_CREDITS_FLOOR)
      : FREE_ACCESS_CREDITS_FLOOR;
    const futurePeriodEnd = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString();

    return {
      ...profile,
      hasFullAccess: true,
      credits: guaranteedCredits,
      subscriptionPlan: profile?.subscriptionPlan || 'UNLIMITED',
      currentPeriodEnd: profile?.currentPeriodEnd || futurePeriodEnd,
      subscriptionStatus: profile?.subscriptionStatus || 'active',
      calculatorLifetimeActive: true,
      calculator: {
        ...(profile?.calculator || {}),
        calculatorLifetimeActive: true
      }
    };
  }, [profile, hasPrivilegedAccess]);
  
  // ESSENTIAL REFS
  const chatEndRef = useRef(null);
  const analysisProcessingRef = useRef(false);
  const sensiProcessingRef = useRef(false);
  const backendInitAttemptedRef = useRef(new Set());

  const clearCoachJobStorage = useCallback(() => {
    localStorage.removeItem(COACH_JOB_STORAGE_KEY);
    localStorage.removeItem(COACH_JOB_TS_STORAGE_KEY);
    localStorage.removeItem(COACH_JOB_UID_STORAGE_KEY);
  }, []);

  const clearSensiJobStorage = useCallback(() => {
    localStorage.removeItem(SENSI_JOB_STORAGE_KEY);
    localStorage.removeItem(SENSI_JOB_TS_STORAGE_KEY);
    localStorage.removeItem(SENSI_JOB_UID_STORAGE_KEY);
  }, []);

  const fetchJobStatusViaApi = useCallback(async (jobType, jobId) => {
    const currentUser = auth.currentUser;
    if (!currentUser || !jobId) throw new Error(t('app_user_not_authenticated'));
    const token = await currentUser.getIdToken();
    const response = await fetch(`${API}/${jobType}/status/${jobId}`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` }
    });

    let payload = {};
    try { payload = await response.json(); } catch { payload = {}; }
    if (!response.ok) {
      const message = payload?.message || payload?.error || `Status polling failed (${response.status})`;
      const error = new Error(message);
      error.status = response.status;
      throw error;
    }
    return payload || {};
  }, [t]);
  const isTerminalDoneStatus = useCallback((status) => {
    const normalized = String(status || '').trim().toLowerCase();
    return normalized === 'done' || normalized === 'completed' || normalized === 'success';
  }, []);
  const isActiveMonitorStatus = useCallback((status) => {
    const normalized = String(status || '').trim().toLowerCase();
    return normalized === 'uploading' || normalized === 'queued' || normalized === 'processing';
  }, []);
  const syncTermsAcceptanceStatus = useCallback(async (firebaseUser) => {
    if (!firebaseUser) return null;
    const cachedAccepted = readCachedTermsAccepted(firebaseUser.uid);
    const lastSyncTs = readCachedTermsStatusSyncTs(firebaseUser.uid);
    const cacheFresh = lastSyncTs > 0 && (Date.now() - lastSyncTs) < TERMS_STATUS_TTL_MS;

    if (cachedAccepted !== null && cacheFresh) {
      setTermsAccepted(cachedAccepted);
      return cachedAccepted;
    }

    try {
      const token = await firebaseUser.getIdToken();
      const response = await fetch(`${API}/legal/status`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) throw new Error(`TERMS_STATUS_${response.status}`);

      const payload = await response.json().catch(() => ({}));
      const userTermsAccepted = payload?.user_terms?.accepted === true;
      const privacyAccepted = payload?.privacy_policy?.accepted === true;
      const canonicalAccepted = userTermsAccepted && privacyAccepted;

      setTermsAccepted(canonicalAccepted);
      writeCachedTermsAccepted(firebaseUser.uid, canonicalAccepted);
      return canonicalAccepted;
    } catch (error) {
      console.warn('[TERMS] Canonical status sync failed:', error?.message || error);
      const cached = readCachedTermsAccepted(firebaseUser.uid);
      if (cached !== null) {
        setTermsAccepted(cached);
        writeCachedTermsStatusSyncTs(firebaseUser.uid);
        return cached;
      }
      setTermsAccepted(false);
      return null;
    }
  }, []);

  useEffect(() => {
    console.log('📱 activeTab:', activeTab);
  }, [activeTab]);

  useEffect(() => {
    const prefersReducedMotion = typeof window !== 'undefined'
      && window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

    requestAnimationFrame(() => {
      const scrollOptions = { top: 0, left: 0, behavior: prefersReducedMotion ? 'auto' : 'smooth' };
      window.scrollTo(scrollOptions);
      document.documentElement.scrollTo?.(scrollOptions);
      document.body.scrollTo?.(scrollOptions);
      document.querySelector('.swipe-navigator')?.scrollTo?.(scrollOptions);
      document.querySelector('.screen-shell')?.scrollIntoView?.({ behavior: prefersReducedMotion ? 'auto' : 'smooth', block: 'start' });
    });
  }, [activeTab]);

  // Referral code: already captured by inline script in index.html
  // Here we just read from localStorage
  useEffect(() => {
    // Privileged perks are applied through effectiveProfile in memory.
    // Writing credits/lifetime flags from the client violates Firestore ownership rules
    // for non-admin privileged accounts and creates noisy permission errors for users.
    if (!user?.uid || !profile || !hasPrivilegedAccess) return;
  }, [user?.uid, profile, hasPrivilegedAccess]);

  const [referralCode, setReferralCode] = useState(() => {
    const saved = localStorage.getItem('referral_code');
    if (saved) console.log('🎯 Referral code from storage:', saved);
    return saved;
  });

  useEffect(() => {
    if (!user) return;

    const now = Date.now();
    const hydrateJob = ({
      storageKey,
      tsKey,
      uidKey,
      expectedPrefix,
      setJobId,
      getCurrentJobId,
      setStatus,
      defaultSubStatus,
      setProcessingRef
    }) => {
      const storedJobId = localStorage.getItem(storageKey);
      const storedTs = Number(localStorage.getItem(tsKey) || 0);
      const storedUid = localStorage.getItem(uidKey);

      if (!storedJobId) {
        if (localStorage.getItem(tsKey)) localStorage.removeItem(tsKey);
        if (localStorage.getItem(uidKey)) localStorage.removeItem(uidKey);
        return;
      }

      const hasExpectedPrefix = expectedPrefix ? String(storedJobId).startsWith(expectedPrefix) : true;
      if (!hasExpectedPrefix) {
        localStorage.removeItem(storageKey);
        localStorage.removeItem(tsKey);
        localStorage.removeItem(uidKey);
        return;
      }

      if (storedUid && storedUid !== user.uid) {
        localStorage.removeItem(storageKey);
        localStorage.removeItem(tsKey);
        localStorage.removeItem(uidKey);
        return;
      }

      const isStale = !storedTs || (now - storedTs) > JOB_REHYDRATE_MAX_AGE_MS;
      if (isStale) {
        localStorage.removeItem(storageKey);
        localStorage.removeItem(tsKey);
        localStorage.removeItem(uidKey);
        return;
      }

      if (getCurrentJobId()) return;
      setJobId(storedJobId);
      setStatus(prev => (
        prev?.status === 'done' || prev?.status === 'error'
          ? prev
          : {
              ...prev,
              status: 'queued',
              progress: Math.max(10, Number(prev?.progress || 0)),
              subStatus: prev?.subStatus || defaultSubStatus
            }
      ));
      if (setProcessingRef) setProcessingRef.current = true;
      setIsProcessing(true);
    };

    hydrateJob({
      storageKey: COACH_JOB_STORAGE_KEY,
      tsKey: COACH_JOB_TS_STORAGE_KEY,
      uidKey: COACH_JOB_UID_STORAGE_KEY,
      expectedPrefix: 'coach_',
      setJobId: setCoachJobId,
      getCurrentJobId: () => coachJobId,
      setStatus: setCoachStatus,
      defaultSubStatus: 'Reconectando monitor de análise...'
    });

    hydrateJob({
      storageKey: SENSI_JOB_STORAGE_KEY,
      tsKey: SENSI_JOB_TS_STORAGE_KEY,
      uidKey: SENSI_JOB_UID_STORAGE_KEY,
      expectedPrefix: 'sensi_',
      setJobId: setSensiJobId,
      getCurrentJobId: () => sensiJobId,
      setStatus: setSensiStatus,
      defaultSubStatus: 'Reconectando monitor de sensi...',
      setProcessingRef: sensiProcessingRef
    });
  }, [user, coachJobId, sensiJobId]);

  // Handle referral and Stripe payment return — synchronize entitlements
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    // Capture Referral (Second layer of protection to index.html)
    const ref = params.get('ref');
    if (ref) {
      const lockedReferral = localStorage.getItem('referral_code');
      if (!lockedReferral) {
        localStorage.setItem('referral_code', ref);
        setReferralCode(ref);
        console.log('🎯 Referral captured from URL (first-touch):', ref);
      } else if (lockedReferral === ref) {
        setReferralCode(ref);
        console.log('🎯 Referral already locked with same code:', ref);
      } else {
        console.log('⏭️ Referral ignored (first-touch lock active):', { lockedReferral, incoming: ref });
      }
    }

    const payment = params.get('payment');
    const sessionId = params.get('session_id');
    const paymentType = params.get('type');

    if (payment === 'success' && sessionId) {
      // Clean URL immediately
      window.history.replaceState({}, '', window.location.pathname);
      if (paymentType === 'freefire_calculator_lifetime') {
        setActiveTab('freefire-calculator');
      } else if (paymentType === 'calculator_lifetime') {
        setActiveTab('calculator');
      } else {
        setActiveTab('hub');
      }

      // Verify payment and synchronize entitlement via backend
      const verifyPayment = async () => {
        startLoading();
        // Wait for auth to be ready
        const waitForAuth = () => new Promise((resolve) => {
          if (auth.currentUser) return resolve(auth.currentUser);
          const unsub = onAuthStateChanged(auth, (u) => {
            if (u) { unsub(); resolve(u); }
          });
          setTimeout(() => resolve(null), 10000);
        });

        try {
          const currentUser = await waitForAuth();
          if (!currentUser) return;

          const token = await currentUser.getIdToken();
          const res = await fetch(`${API}/stripe/sync-checkout-session`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ sessionId })
          });

          if (!res.ok) {
            console.error('Failed to sync payment session');
            return;
          }

          const data = await res.json();
          if (data.status === 'success' || data.status === 'already_processed') {
            if (window.refreshUserEntitlements) {
              await window.refreshUserEntitlements({ force: true, reason: 'stripe_success' });
            }
            alert(t('app_payment_confirmed'));
          } else if (data.status === 'pending') {
            alert(t('app_payment_processing'));
          }
        } catch (err) {
          console.error('Payment verification error:', err);
          alert(t('app_payment_received'));
        } finally {
          stopLoading();
        }
      };

      verifyPayment();
    } else if (payment === 'success') {
      // Fallback for old sessions without session_id
      window.history.replaceState({}, '', window.location.pathname);
      setActiveTab('hub');
      alert(t('app_payment_received'));
    } else if (payment === 'cancel') {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [startLoading, stopLoading, setActiveTab, t, lang]); // Added lang/t for alerts

  useEffect(() => {
    let profileUnsubscribe = null;
    let entitlementsUnsubscribe = null;

    const unsubscribe = onAuthStateChanged(auth, async (u) => {
      try {
        if (profileUnsubscribe) { profileUnsubscribe(); profileUnsubscribe = null; }
        if (entitlementsUnsubscribe) { entitlementsUnsubscribe(); entitlementsUnsubscribe = null; }

        setProfile(null);
        setAnalyses([]);
        setSocialFeed([]);
        setSocialLeaderboards({ daily: [], weekly: [], all_time: [] });
        setChatMessages([]);
        setCurrentAnalysis(null);
        setTermsAccepted(null);

        setAuthInitialized(true);
        setUser(u);
        if (u) {
          const cachedTerms = readCachedTermsAccepted(u.uid);
          if (cachedTerms !== null) {
            setTermsAccepted(cachedTerms);
          }
          syncTermsAcceptanceStatus(u).catch((err) => {
            console.warn('[TERMS] Initial sync failed:', err?.message || err);
          });

          console.log('🔐 [AUTH] Authenticated:', u.email);
          u.getIdTokenResult().then(tokenResult => {
            u.claims = tokenResult.claims;
            setUser(Object.assign(Object.create(Object.getPrototypeOf(u)), u));
          });
          
          if (window.refreshUserEntitlements) {
            window.refreshUserEntitlements({ force: true, reason: 'login' }).catch(console.warn);
          }

          // 1. Profile Listener
          profileUnsubscribe = onSnapshot(
            doc(db, 'users', u.uid),
            (snap) => {
              if (snap.exists()) {
                const data = snap.data();
                const rawMainGame = data.mainGame;
                const rawLegacyGame = data.main_game;
                const hasAnyBoundGame = Boolean(rawMainGame || rawLegacyGame);
                const localOnboardingDone = localStorage.getItem(ONBOARDING_DONE_KEY) === '1';
                const localGameValue = localStorage.getItem(ONBOARDING_GAME_KEY) || localStorage.getItem('selected_game') || '';
                const binding = resolveRuntimeCanonicalGame({
                  rawMainGame,
                  rawLegacyGame,
                  localGame: localGameValue
                });
                const hasValidRemoteBinding = Boolean(binding.canonicalRemoteGame);
                const resolvedCanonicalGame = binding.resolvedCanonicalGame;
                const resolvedLegacyGame = toLegacyGameContext(resolvedCanonicalGame);

                setProfile(prev => ({
                  ...data,
                  mainGame: resolvedCanonicalGame,
                  main_game: resolvedLegacyGame,
                  entitlements: prev?.entitlements || createDefaultFreeEntitlements()
                }));
                if (data.termsAccepted !== undefined) {
                  const legacyAccepted = Boolean(data.termsAccepted);
                  if (legacyAccepted) {
                    setTermsAccepted(true);
                    writeCachedTermsAccepted(u.uid, true);
                  }
                }
                const shouldPersistResolvedGame = hasAnyBoundGame || Boolean(binding.canonicalLocalGame);
                if (shouldPersistResolvedGame) {
                  const gameUpdates = {};
                  if (cleanString(rawMainGame) !== resolvedCanonicalGame) gameUpdates.mainGame = resolvedCanonicalGame;
                  if (cleanString(rawLegacyGame) !== resolvedLegacyGame) gameUpdates.main_game = resolvedLegacyGame;
                  if (Object.keys(gameUpdates).length > 0) {
                    updateDoc(doc(db, 'users', u.uid), gameUpdates)
                      .catch((err) => console.warn('[GAME-BINDING] Failed to backfill game binding:', err?.message || err));
                  }
                }
                // Onboarding decision: Firestore onboardingCompleted is the primary source of truth.
                // Legacy users with a mainGame but no onboardingCompleted field are considered onboarded.
                const remoteOnboardingCompleted = data.onboardingCompleted === true;
                const isLegacyOnboarded = !remoteOnboardingCompleted && hasValidRemoteBinding;
                const isOnboardingDone = remoteOnboardingCompleted || isLegacyOnboarded;
                
                if (isOnboardingDone) {
                  localStorage.setItem(ONBOARDING_DONE_KEY, '1');
                  localStorage.setItem(ONBOARDING_GAME_KEY, resolvedCanonicalGame);
                  localStorage.setItem('gph_main_game', resolvedCanonicalGame);
                  localStorage.setItem('mi_game_context', resolvedCanonicalGame);
                  localStorage.setItem('selected_game', resolvedCanonicalGame);
                }
                const shouldShowOnboarding = !isOnboardingDone;
                setShowOnboarding(shouldShowOnboarding);
                setGameContext(resolvedLegacyGame);
              } else {
                // Cost guard: only initialize backend user when doc is missing.
                if (!backendInitAttemptedRef.current.has(u.uid)) {
                  backendInitAttemptedRef.current.add(u.uid);
                  initUserOnBackend(u, referralCode);
                }
                const localOnboardingDone = localStorage.getItem(ONBOARDING_DONE_KEY) === '1';
                const localCanonicalGame = canonicalGameId(localStorage.getItem(ONBOARDING_GAME_KEY) || localStorage.getItem('selected_game') || 'pubg_mobile');
                const shouldShowOnboarding = !(localOnboardingDone && Boolean(localCanonicalGame));
                setShowOnboarding(shouldShowOnboarding);
                setGameContext(toLegacyGameContext(localCanonicalGame));
                const cachedTerms = readCachedTermsAccepted(u.uid);
                if (cachedTerms !== null) setTermsAccepted(cachedTerms);
              }
            },
            (error) => {
              console.error('❌ [PROFILE] Listener failed:', error);
              const localCanonicalGame = canonicalGameId(
                localStorage.getItem(ONBOARDING_GAME_KEY) || localStorage.getItem('selected_game') || 'pubg_mobile',
                'pubg_mobile'
              );
              setProfile(prev => prev || {
                uid: u.uid,
                email: u.email,
                displayName: u.displayName || 'Player',
                mainGame: localCanonicalGame,
                main_game: toLegacyGameContext(localCanonicalGame)
              });
              const cachedTerms = readCachedTermsAccepted(u.uid);
              if (cachedTerms !== null) setTermsAccepted(cachedTerms);
              setShowOnboarding(localStorage.getItem(ONBOARDING_DONE_KEY) !== '1');
              setGameContext(toLegacyGameContext(localCanonicalGame));
            }
          );

          // 1.5. Entitlements Listener
          entitlementsUnsubscribe = onSnapshot(
            doc(db, 'users', u.uid, 'entitlements', 'current'),
            (snap) => {
              if (snap.exists()) {
                const entData = normalizeEntitlements(snap.data());
                setProfile(prev => prev ? { ...prev, entitlements: entData } : prev);
              } else {
                setProfile(prev => prev ? { ...prev, entitlements: createDefaultFreeEntitlements() } : prev);
              }
            },
            (error) => {
              console.error('❌ [ENTITLEMENTS] Listener failed:', error);
            }
          );

        }
      } catch (error) {
        console.error('Auth sync error:', error);
      }
    });

    return () => {
      if (profileUnsubscribe) profileUnsubscribe();
      unsubscribe();
    };
  }, [referralCode, syncTermsAcceptanceStatus]);

  useEffect(() => {
    const preferredLanguage = profile?.preferredLanguage || profile?.language;
    if (!preferredLanguage) return;
    if (preferredLanguage !== language) {
      changeLanguage(preferredLanguage);
    }
  }, [profile?.preferredLanguage, profile?.language, language, changeLanguage]);

  useEffect(() => {
    const leaderboardDefaults = { daily: [], weekly: [], all_time: [] };
    const shouldMountRuntimeData = Boolean(user) && termsAccepted === true && showOnboarding !== true;
    const shouldListenAnalyses = shouldMountRuntimeData && ['home', 'coach', 'training', 'settings', 'profile', 'history'].includes(activeTab);
    const shouldListenSocial = false;

    let analysesUnsubscribe = null;
    let socialFeedUnsubscribe = null;
    let socialLeaderboardUnsubscribe = null;

    const toMillis = (value) => {
      if (!value) return 0;
      if (typeof value?.toMillis === 'function') return value.toMillis();
      if (typeof value?.seconds === 'number') {
        const nanos = typeof value?.nanoseconds === 'number' ? value.nanoseconds : 0;
        return (value.seconds * 1000) + Math.floor(nanos / 1_000_000);
      }
      if (value instanceof Date) return value.getTime();
      if (typeof value === 'string' || typeof value === 'number') {
        const parsed = new Date(value).getTime();
        return Number.isFinite(parsed) ? parsed : 0;
      }
      return 0;
    };

    if (!shouldListenAnalyses) {
      setAnalyses([]);
    } else if (user?.uid) {
      const analysesQuery = query(
        collection(db, 'analyses'),
        where('userId', '==', user.uid),
        orderBy('createdAt', 'desc'),
        limit(10)
      );

      analysesUnsubscribe = onSnapshot(
        analysesQuery,
        (snapshot) => {
          const docs = snapshot.docs.map((d) => normalizeAnalysis(
            (() => {
              const raw = d.data() || {};
              const inferredType =
                raw.type ||
                raw.analysisType ||
                raw.result?.type ||
                (Array.isArray(raw?.result?.scopes) ? 'sensi' : 'coach');
              return { id: d.id, ...raw, type: inferredType };
            })(),
            {
              fallbackId: d.id,
              fallbackUserId: user.uid,
              fallbackType: 'coach',
              fallbackPipeline: 'frontend_analyses_listener'
            }
          ));

          docs.sort((a, b) => {
            const createdDiff = toMillis(b.createdAt) - toMillis(a.createdAt);
            if (createdDiff !== 0) return createdDiff;
            const updatedDiff = toMillis(b.updatedAt) - toMillis(a.updatedAt);
            if (updatedDiff !== 0) return updatedDiff;
            return String(b.id || '').localeCompare(String(a.id || ''));
          });

          setAnalyses(docs.slice(0, 10));
        },
        (error) => {
          const msg = (error?.message || '').toLowerCase();
          const isMissingIndex = error?.code === 'failed-precondition' || msg.includes('index');
          if (isMissingIndex) {
            console.error('❌ [ANALYSES] Missing composite index for query (userId + createdAt desc).');
            setAnalyses([]);
            return;
          }
          console.error('❌ [ANALYSES] Listener failed:', error);
          setAnalyses([]);
        }
      );
    }

    if (!shouldListenSocial) {
      setSocialFeed([]);
      setSocialLeaderboards(leaderboardDefaults);
    } else {
      const socialFeedQuery = query(
        collection(db, 'social_feed'),
        orderBy('createdAt', 'desc'),
        limit(24)
      );
      socialFeedUnsubscribe = onSnapshot(
        socialFeedQuery,
        (snapshot) => {
          const docs = snapshot.docs.map((d) => {
            const data = d.data() || {};
            return {
              id: d.id,
              userId: data.userId || '',
              displayName: data.displayName || 'Player',
              avatarUrl: data.avatarUrl || '',
              gameId: data.gameId || 'pubg_mobile',
              analysisType: data.analysisType || 'coach',
              score: Number(data.score || 0),
              delta: Number(data.delta || 0),
              merits: Array.isArray(data.merits) ? data.merits : [],
              summary: data.summary || '',
              createdAt: data.createdAt || null,
              createdAtEpoch: Number(data.createdAtEpoch || 0),
              analysisId: data.analysisId || d.id
            };
          });
          setSocialFeed(docs);
        },
        (error) => {
          console.error('❌ [SOCIAL-FEED] Listener failed:', error);
          setSocialFeed([]);
        }
      );

      const leaderboardQuery = query(
        collection(db, 'social_leaderboards'),
        where(documentId(), 'in', ['daily', 'weekly', 'all_time'])
      );
      socialLeaderboardUnsubscribe = onSnapshot(
        leaderboardQuery,
        (snapshot) => {
          const nextBoards = { ...leaderboardDefaults };
          snapshot.forEach((docSnap) => {
            const key = docSnap.id;
            if (!(key in nextBoards)) return;
            const payload = docSnap.data() || {};
            nextBoards[key] = Array.isArray(payload.entries) ? payload.entries : [];
          });
          setSocialLeaderboards(nextBoards);
        },
        (error) => {
          console.error('❌ [SOCIAL-LEADERBOARD] Listener failed:', error);
          setSocialLeaderboards(leaderboardDefaults);
        }
      );
    }

    return () => {
      if (analysesUnsubscribe) analysesUnsubscribe();
      if (socialFeedUnsubscribe) socialFeedUnsubscribe();
      if (socialLeaderboardUnsubscribe) socialLeaderboardUnsubscribe();
    };
  }, [activeTab, showOnboarding, termsAccepted, user]);

  // --- ANALYSIS REAL-TIME HUD (P0 PUSH ARCHITECTURE) ---
  
  // 1. COACH LISTENER
  useEffect(() => {
    if (!user || !coachJobId) return;
    
    console.log('📡 [MIRAI-HUD] Subscribed to Coach Job:', coachJobId);
    let closed = false;
    let pollInterval = null;
    let consecutivePollErrors = 0;
    let hasWarnedStall = false;

    const clearPolling = () => {
      if (pollInterval) {
        clearInterval(pollInterval);
        pollInterval = null;
      }
    };

    const applyCoachJobState = (data, source = 'listener') => {
      const rawStatus = data?.status || 'queued';
      const status = isTerminalDoneStatus(rawStatus) ? 'done' : rawStatus;
      if (isActiveMonitorStatus(status)) {
        localStorage.setItem(COACH_JOB_TS_STORAGE_KEY, String(Date.now()));
      }
      setCoachStatus(prev => ({
        ...prev,
        status,
        progress: Number(data?.progress || prev?.progress || 10),
        subStatus: data?.subStatus || (source === 'polling' ? t('app_job_syncing') : t('app_analyzing')),
        error: status === 'error' ? (data?.error || prev?.error || null) : null
      }));

      if (status === 'done') {
        const normalizedCoachResult = normalizeAnalysis(
          data?.result || {},
          {
            fallbackId: data?.result?.id || coachJobId,
            fallbackUserId: user.uid,
            fallbackType: 'coach',
            fallbackGame: gameContext,
            fallbackStatus: status,
            fallbackPipeline: source === 'polling' ? 'frontend_job_polling_fallback' : 'frontend_job_listener'
          }
        );
        setCoachStatus(prev => ({ ...prev, result: normalizedCoachResult, jobId: coachJobId }));
        setCurrentAnalysis(normalizedCoachResult);
        setIsProcessing(false);
        setCoachJobId(null);
        clearCoachJobStorage();
        clearPolling();
      } else if (status === 'error') {
        setIsProcessing(false);
        setCoachJobId(null);
        clearCoachJobStorage();
        clearPolling();
      }
    };

    const pollOnce = async () => {
      try {
        const polled = await fetchJobStatusViaApi('coach', coachJobId);
        consecutivePollErrors = 0;
        applyCoachJobState(polled, 'polling');
        if ((isTerminalDoneStatus(polled?.status) || polled?.status === 'error') && pollInterval) {
          clearPolling();
        }
      } catch (pollErr) {
        consecutivePollErrors += 1;
        if (closed) return;
        console.error('❌ [MIRAI-HUD] Coach polling failed:', pollErr);
        setCoachStatus(prev => ({
          ...prev,
          status: isActiveMonitorStatus(prev?.status) ? prev.status : 'queued',
          error: null,
          subStatus: t('app_reconnecting_analysis_count', { count: consecutivePollErrors })
        }));
      }
    };

    const startPollingFallback = (reason) => {
      if (closed || pollInterval) return;
      console.warn(`⚠️ [MIRAI-HUD] Coach listener fallback polling activated: ${reason}`);
      setCoachStatus(prev => ({
        ...prev,
        status: isActiveMonitorStatus(prev?.status) ? prev.status : 'queued',
        error: null,
        subStatus: t('app_analysis_monitor_fallback')
      }));
      pollOnce();
      pollInterval = setInterval(pollOnce, JOB_POLL_INTERVAL_MS);
    };
    
    // Stall guard: never abort job automatically; keep monitor alive and switch to safe polling.
    const stallWarningTimer = setTimeout(() => {
      if (closed || hasWarnedStall) return;
      hasWarnedStall = true;
      console.warn('⚠️ [MIRAI-HUD] Coach job still running after stall threshold. Keeping monitor active.');
      setCoachStatus(prev => (
        prev.status === 'done' || prev.status === 'error'
          ? prev
          : {
              ...prev,
              status: isActiveMonitorStatus(prev?.status) ? prev.status : 'queued',
              error: null,
              subStatus: t('app_analysis_background_sync')
            }
      ));
      startPollingFallback('stall_guard');
    }, JOB_MONITOR_STALL_WARN_MS);

    const unsub = onSnapshot(
      doc(db, 'analysis_jobs', coachJobId),
      (snap) => {
        if (!snap.exists()) {
          startPollingFallback('doc_missing');
          return;
        }
        applyCoachJobState(snap.data(), 'listener');
      },
      (listenerError) => {
        console.error('❌ [MIRAI-HUD] Coach listener failed:', listenerError);
        setCoachStatus(prev => ({
          ...prev,
          subStatus: t('app_reconnecting_analysis_monitor'),
          error: null
        }));
        startPollingFallback(listenerError?.code || 'listener_error');
      }
    );

    return () => {
      closed = true;
      unsub();
      clearTimeout(stallWarningTimer);
      clearPolling();
    };
  }, [user, coachJobId, gameContext, fetchJobStatusViaApi, clearCoachJobStorage, isTerminalDoneStatus, isActiveMonitorStatus, t]);

  // 2. SENSI LISTENER
  useEffect(() => {
    if (!user || !sensiJobId) return;

    console.log('📡 [MIRAI-HUD] Subscribed to Sensi Job:', sensiJobId);
    let closed = false;
    let pollInterval = null;
    let consecutivePollErrors = 0;
    let hasWarnedStall = false;

    const clearPolling = () => {
      if (pollInterval) {
        clearInterval(pollInterval);
        pollInterval = null;
      }
    };

    const applySensiJobState = (data, source = 'listener') => {
      const rawStatus = data?.status || 'queued';
      const status = isTerminalDoneStatus(rawStatus) ? 'done' : rawStatus;
      if (isActiveMonitorStatus(status)) {
        localStorage.setItem(SENSI_JOB_TS_STORAGE_KEY, String(Date.now()));
      }
      if (status !== 'error') {
        setSensiError(null);
        setSensiStall(false);
      }
      setSensiStatus(prev => ({
        ...prev,
        status,
        progress: Number(data?.progress || prev?.progress || 10),
        subStatus: data?.subStatus || (source === 'polling' ? t('app_sensi_status_syncing') : t('app_syncing')),
        error: status === 'error' ? (data?.message || data?.error || prev?.error || null) : null,
        message: status === 'error' ? (data?.message || data?.error || prev?.message || null) : null
      }));

      if (status === 'done') {
        const normalizedSensiResult = normalizeAnalysis(
          data?.result || {},
          {
            fallbackId: data?.result?.id || sensiJobId,
            fallbackUserId: user.uid,
            fallbackType: 'sensi',
            fallbackGame: gameContext,
            fallbackStatus: status,
            fallbackPipeline: source === 'polling' ? 'frontend_job_polling_fallback' : 'frontend_job_listener'
          }
        );
        setSensiStatus(prev => ({ ...prev, result: normalizedSensiResult, jobId: sensiJobId }));
        sensiProcessingRef.current = false;
        setIsProcessing(false);
        setSensiJobId(null);
        clearSensiJobStorage();
        clearPolling();
      } else if (status === 'error') {
        setSensiError(data?.message || data?.error || t('app_sensi_processing_failed'));
        setSensiStall(true);
        sensiProcessingRef.current = false;
        setIsProcessing(false);
        setSensiJobId(null);
        clearSensiJobStorage();
        clearPolling();
      }
    };

    const pollOnce = async () => {
      try {
        const polled = await fetchJobStatusViaApi('sensi', sensiJobId);
        consecutivePollErrors = 0;
        applySensiJobState(polled, 'polling');
        if ((isTerminalDoneStatus(polled?.status) || polled?.status === 'error') && pollInterval) {
          clearPolling();
        }
      } catch (pollErr) {
        consecutivePollErrors += 1;
        if (closed) return;
        console.error('❌ [MIRAI-HUD] Sensi polling failed:', pollErr);
        setSensiStatus(prev => ({
          ...prev,
          status: isActiveMonitorStatus(prev?.status) ? prev.status : 'queued',
          error: null,
          subStatus: t('app_reconnecting_sensi_count', { count: consecutivePollErrors })
        }));
        setSensiError(null);
        setSensiStall(true);
      }
    };

    const startPollingFallback = (reason) => {
      if (closed || pollInterval) return;
      const isPermissionIssue = reason === 'permission-denied' || String(reason || '').includes('permission');
      const fallbackMessage = isPermissionIssue
        ? t('app_realtime_monitor_permission_fallback')
        : t('app_realtime_monitor_polling_fallback');
      console.warn(`⚠️ [MIRAI-HUD] Sensi listener fallback polling activated: ${reason}`);
      setSensiError(null);
      setSensiStall(true);
      setSensiStatus(prev => ({
        ...prev,
        status: isActiveMonitorStatus(prev?.status) ? prev.status : 'queued',
        error: null,
        subStatus: fallbackMessage
      }));
      pollOnce();
      pollInterval = setInterval(pollOnce, JOB_POLL_INTERVAL_MS);
    };

    const stallWarningTimer = setTimeout(() => {
      if (closed || hasWarnedStall) return;
      hasWarnedStall = true;
      console.warn('⚠️ [MIRAI-HUD] Sensi job still running after stall threshold. Keeping monitor active.');
      setSensiStatus(prev => (
        prev.status === 'done' || prev.status === 'error'
          ? prev
          : {
              ...prev,
              status: isActiveMonitorStatus(prev?.status) ? prev.status : 'queued',
              error: null,
              subStatus: t('app_sensi_background_sync')
            }
      ));
      setSensiError(null);
      setSensiStall(true);
      startPollingFallback('stall_guard');
    }, JOB_MONITOR_STALL_WARN_MS);

    const unsub = onSnapshot(
      doc(db, 'analysis_jobs', sensiJobId),
      (snap) => {
        if (!snap.exists()) {
          startPollingFallback('doc_missing');
          return;
        }
        applySensiJobState(snap.data(), 'listener');
      },
      (listenerError) => {
        console.error('❌ [MIRAI-HUD] Sensi listener failed:', listenerError);
        setSensiStatus(prev => ({
          ...prev,
          subStatus: t('app_reconnecting_sensi_monitor'),
          error: null
        }));
        setSensiError(listenerError?.message || t('app_sensi_monitor_unavailable'));
        setSensiStall(true);
        startPollingFallback(listenerError?.code || 'listener_error');
      }
    );

    return () => {
      closed = true;
      unsub();
      clearTimeout(stallWarningTimer);
      clearPolling();
    };
  }, [user, sensiJobId, gameContext, fetchJobStatusViaApi, clearSensiJobStorage, isTerminalDoneStatus, isActiveMonitorStatus, t]);

  // 3. RESUME/FOREGROUND SYNC (tab minimize, screen lock, app background)
  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    const syncActiveJobsNow = async (reason = 'foreground') => {
      if (cancelled) return;

      const activeCoach = coachJobId || localStorage.getItem(COACH_JOB_STORAGE_KEY);
      const activeSensi = sensiJobId || localStorage.getItem(SENSI_JOB_STORAGE_KEY);
      if (!activeCoach && !activeSensi) return;

      if (activeCoach && !coachJobId) {
        setCoachJobId(activeCoach);
        setIsProcessing(true);
        setCoachStatus(prev => ({
          ...prev,
          status: isActiveMonitorStatus(prev?.status) ? prev.status : 'queued',
          progress: Math.max(10, Number(prev?.progress || 0)),
          subStatus: prev?.subStatus || t('app_reconnecting_after_return'),
          error: null
        }));
      }

      if (activeSensi && !sensiJobId) {
        setSensiJobId(activeSensi);
        sensiProcessingRef.current = true;
        setIsProcessing(true);
        setSensiStatus(prev => ({
          ...prev,
          status: isActiveMonitorStatus(prev?.status) ? prev.status : 'queued',
          progress: Math.max(10, Number(prev?.progress || 0)),
          subStatus: prev?.subStatus || t('app_reconnecting_sensi_after_return'),
          error: null
        }));
      }

      if (activeCoach) {
        try {
          const data = await fetchJobStatusViaApi('coach', activeCoach);
          if (cancelled) return;
          const rawStatus = data?.status || 'queued';
          const status = isTerminalDoneStatus(rawStatus) ? 'done' : rawStatus;

          if (status === 'done') {
            const normalizedCoachResult = normalizeAnalysis(
              data?.result || {},
              {
                fallbackId: data?.result?.id || activeCoach,
                fallbackUserId: user.uid,
                fallbackType: 'coach',
                fallbackGame: gameContext,
                fallbackStatus: status,
                fallbackPipeline: 'frontend_resume_sync'
              }
            );
            setCoachStatus(prev => ({ ...prev, status: 'done', progress: 100, result: normalizedCoachResult, jobId: activeCoach, error: null }));
            setCurrentAnalysis(normalizedCoachResult);
            setIsProcessing(false);
            setCoachJobId(null);
            clearCoachJobStorage();
          } else if (status === 'error') {
            setCoachStatus(prev => ({ ...prev, status: 'error', progress: 100, error: data?.error || t('app_analysis_processing_failed') }));
            setIsProcessing(false);
            setCoachJobId(null);
            clearCoachJobStorage();
          } else {
            localStorage.setItem(COACH_JOB_TS_STORAGE_KEY, String(Date.now()));
            setIsProcessing(true);
            setCoachStatus(prev => ({
              ...prev,
              status,
              progress: Number(data?.progress || prev?.progress || 10),
              subStatus: data?.subStatus || t('app_analysis_in_progress_reason', { reason }),
              error: null
            }));
          }
        } catch (error) {
          console.warn('[MIRAI-HUD] Coach resume sync failed:', error?.message || error);
          if (!cancelled) {
            setIsProcessing(true);
            setCoachStatus(prev => ({
              ...prev,
              status: isActiveMonitorStatus(prev?.status) ? prev.status : 'queued',
              error: null,
              subStatus: t('app_connection_restored_analysis'),
            }));
          }
        }
      }

      if (activeSensi) {
        try {
          const data = await fetchJobStatusViaApi('sensi', activeSensi);
          if (cancelled) return;
          const rawStatus = data?.status || 'queued';
          const status = isTerminalDoneStatus(rawStatus) ? 'done' : rawStatus;

          if (status === 'done') {
            const normalizedSensiResult = normalizeAnalysis(
              data?.result || {},
              {
                fallbackId: data?.result?.id || activeSensi,
                fallbackUserId: user.uid,
                fallbackType: 'sensi',
                fallbackGame: gameContext,
                fallbackStatus: status,
                fallbackPipeline: 'frontend_resume_sync'
              }
            );
            setSensiStatus(prev => ({ ...prev, status: 'done', progress: 100, result: normalizedSensiResult, jobId: activeSensi, error: null }));
            sensiProcessingRef.current = false;
            setSensiError(null);
            setSensiStall(false);
            setIsProcessing(false);
            setSensiJobId(null);
            clearSensiJobStorage();
          } else if (status === 'error') {
            setSensiStatus(prev => ({
              ...prev,
              status: 'error',
              progress: 100,
              error: data?.message || data?.error || t('app_sensi_processing_failed'),
              message: data?.message || data?.error || t('app_sensi_processing_failed')
            }));
            setSensiError(data?.message || data?.error || t('app_sensi_processing_failed'));
            setSensiStall(true);
            sensiProcessingRef.current = false;
            setIsProcessing(false);
            setSensiJobId(null);
            clearSensiJobStorage();
          } else {
            localStorage.setItem(SENSI_JOB_TS_STORAGE_KEY, String(Date.now()));
            sensiProcessingRef.current = true;
            setIsProcessing(true);
            setSensiError(null);
            setSensiStall(false);
            setSensiStatus(prev => ({
              ...prev,
              status,
              progress: Number(data?.progress || prev?.progress || 10),
              subStatus: data?.subStatus || t('app_sensi_in_progress_reason', { reason }),
              error: null
            }));
          }
        } catch (error) {
          console.warn('[MIRAI-HUD] Sensi resume sync failed:', error?.message || error);
          if (!cancelled) {
            setIsProcessing(true);
            sensiProcessingRef.current = true;
            setSensiStall(true);
            setSensiError(null);
            setSensiStatus(prev => ({
              ...prev,
              status: isActiveMonitorStatus(prev?.status) ? prev.status : 'queued',
              error: null,
              subStatus: t('app_connection_restored_sensi')
            }));
          }
        }
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        syncActiveJobsNow('visibility');
      }
    };
    const handleWindowFocus = () => syncActiveJobsNow('focus');
    const handleWindowOnline = () => syncActiveJobsNow('online');

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleWindowFocus);
    window.addEventListener('online', handleWindowOnline);
    window.addEventListener('pageshow', handleWindowFocus);

    syncActiveJobsNow('mount');

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleWindowFocus);
      window.removeEventListener('online', handleWindowOnline);
      window.removeEventListener('pageshow', handleWindowFocus);
    };
  }, [
    user,
    coachJobId,
    sensiJobId,
    gameContext,
    fetchJobStatusViaApi,
    clearCoachJobStorage,
    clearSensiJobStorage,
    isTerminalDoneStatus,
    isActiveMonitorStatus,
    t
  ]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  // Legal acceptance is tracked directly on Firebase user profile.

  const callApi = async (endpoint, body) => {
    if (!auth.currentUser) {
      throw new Error(t('app_user_not_authenticated'));
    }

    const token = await auth.currentUser.getIdToken();
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      let data = {};
      try { data = await response.json(); } catch { /* non-JSON error body (e.g. 502 HTML) */ }
      const detail = data?.detail && typeof data.detail === 'object' ? data.detail : null;
      const errorCode = detail?.error || data?.error?.error || data?.error || null;
      const errorMessage =
        detail?.message ||
        data?.message ||
        data?.error?.message ||
        (typeof data?.error === 'string' ? data.error : null) ||
        `Erro ${response.status}`;
      if (errorCode === 'NOT_ENOUGH_CREDITS') {
        if (hasPrivilegedAccess) {
          const err = new Error(t('app_privileged_retry'));
          err.status = response.status;
          err.code = errorCode;
          throw err;
        }
        // Mi-native coin guide: no blocking popup, just inline messaging
        const costMsg = errorMessage || '';
        setChatMessages(prev => [...prev, {
          role: 'model',
          text: t('app_coach_pro_required_message', { costMsg }),
          mode: 'GUIDE',
          suggestions: [
            { label: t('app_view_plans'), value: '__open_pricing__' },
            { label: t('app_back'), value: 'voltar ao hub' },
          ],
        }]);
      }
      const err = new Error(errorMessage);
      err.status = response.status;
      err.code = errorCode;
      throw err;
    }

    try { return await response.json(); } catch { return {}; }
  };

  const checkoutErrorMessage = (error) => {
    const code = String(error?.code || '').toUpperCase();
    if (code === 'PRODUCT_UNAVAILABLE' || error?.status === 403) {
      return t('checkout_error_unavailable_product');
    }
    if (!error?.status && /network|failed|fetch/i.test(String(error?.message || ''))) {
      return t('checkout_error_connection');
    }
    return t('checkout_error_open');
  };

  const createBillingCheckout = async (productCode, referralCode) => {
    try {
      return await callApi(`${API}/billing/checkout`, { productCode, referralCode });
    } catch (error) {
      if (error?.status === 404) {
        return await callApi(`${API}/create-checkout-session`, { productCode, referralCode });
      }
      throw error;
    }
  };

  const COACH_ANALYSIS_COST = 0; // Must match backend COACH_ANALYSIS_VIDEO_COST

  const handleAnalyze = async (userContext = '', game = 'pubg_mobile', extra = {}) => {
    if (analysisProcessingRef.current) return;

    // Frontend soft-guard: backend remains authoritative for premium access
    // The backend is the authoritative source of truth; this is UX ergonomics only.
    if (!hasPrivilegedAccess) {
      const currentCredits = Number(effectiveProfile?.credits || 0);
      if (false) {
        setChatMessages(prev => [...prev, {
          role: 'model',
          text: t('app_coach_credit_required_message', { cost: COACH_ANALYSIS_COST, credits: currentCredits }),
          mode: 'GUIDE',
          suggestions: [
            { label: t('app_view_plans'), value: '__open_pricing__' },
            { label: t('app_back'), value: 'voltar ao hub' },
          ],
        }]);
        setActiveTab('chat');
        return;
      }
    }

    analysisProcessingRef.current = true;

    setIsProcessing(true);
    startLoading();
    setCurrentAnalysis(null);
    setCoachStatus({ status: 'uploading', progress: 5, subStatus: t('app_analysis_starting') });

    try {

      const token = await auth.currentUser.getIdToken();

      // NEW RESILIENT UPLOADER (CHUNKED)
      const selectedCanonicalGame = canonicalGameId(game);
      const fileToUpload = extra.file || selectedVideo;
      const analysisType = String(extra.analysisType || 'coach').toLowerCase();
      const isMiAnalysis = analysisType === 'mi_analysis';

      const MAX_VIDEO_MB = 200;
      if (fileToUpload && fileToUpload.size > MAX_VIDEO_MB * 1024 * 1024) {
        setIsProcessing(false);
        stopLoading();
        analysisProcessingRef.current = false;
        setErrorModal({
          show: true,
          title: t('app_file_too_large_title'),
          message: t('app_coach_video_limit_message', { maxMb: MAX_VIDEO_MB }),
          kind: 'upload_job'
        });
        return;
      }

      const uploader = new ResilientUploader(fileToUpload, {
        token,
        chat_title: isMiAnalysis ? t('app_mi_analysis_title') : t('chat_title'),
        chat_empty_title: isMiAnalysis ? t('app_mi_analysis_ready') : t('chat_empty_title'),
        type: isMiAnalysis ? 'mi_analysis' : 'coach',
        game: selectedCanonicalGame,
        language,
        context: JSON.stringify({
          analysis_source: isMiAnalysis ? 'mi_analysis_screen' : 'coach',
          ...userContext,
          ...extra.coachLaunchContext
        }),
        trimMeta: extra.trimMeta || null,
        onProgress: (progress, subStatus) => {
          setCoachStatus(prev => ({ ...prev, status: 'uploading', progress, subStatus }));
          // Once we have actual progress, we can stop the global loader to show the specialized UI
          if (progress > 0) stopLoading();
        },
        onStatus: (subStatus) => {
          setCoachStatus(prev => ({ ...prev, subStatus }));
        },
        onError: (message) => {
          setIsProcessing(false);
          stopLoading();
          setErrorModal({
            show: true,
            title: t('error_upload_video'),
            message: safeUiText(
              message,
              t('error_analysis_unavailable')
            ),
            kind: 'upload_job'
          });
        },
        onComplete: (jobId) => {
          setCoachStatus({ status: 'queued', progress: 10, subStatus: t('app_syncing_mi') });
          setCoachJobId(jobId);
          localStorage.setItem(COACH_JOB_STORAGE_KEY, jobId);
          localStorage.setItem(COACH_JOB_TS_STORAGE_KEY, String(Date.now()));
          localStorage.setItem(COACH_JOB_UID_STORAGE_KEY, auth.currentUser?.uid || '');
        }
      });

      await uploader.start();

    } catch (error) {
      console.error('Analysis error:', error);
      const rawErrorMessage = safeUiText(error?.message, '');
      const errCode = error?.code || '';

      if (errCode === 'NOT_ENOUGH_CREDITS' || /saldo insuficiente|not_enough_credits/i.test(errCode)) {
        setChatMessages(prev => [...prev, {
          role: 'model',
          text: t('app_coach_credit_error_message', { cost: COACH_ANALYSIS_COST, error: rawErrorMessage || t('app_insufficient_balance') }),
          mode: 'GUIDE',
          suggestions: [
            { label: t('app_view_plans'), value: '__open_pricing__' },
            { label: t('app_back'), value: 'voltar ao hub' },
          ],
        }]);
        setActiveTab('chat');
      } else if (errCode === 'EMAIL_NOT_VERIFIED') {
        setErrorModal({
          show: true,
          title: t('app_email_not_verified_title'),
          message: t('app_email_not_verified_coach'),
          kind: 'upload_job'
        });
      } else {
        const isCreditsError = /saldo insuficiente|insufficient|Acesso Pro|coins|credits/i.test(rawErrorMessage);
        setErrorModal({
          show: true,
          title: isCreditsError
            ? t('error_analysis_title')
            : t('error_upload_video'),
          message: isCreditsError
            ? safeUiText(rawErrorMessage, t('error_analysis_unavailable'))
            : t('error_analysis_unavailable'),
          kind: isCreditsError ? 'premium_access' : 'upload_job'
        });
      }
      setIsProcessing(false);
      stopLoading();
    } finally {
      analysisProcessingRef.current = false;
    }

  };

  const handleSensiAnalyze = async (file, contextData) => {
    const sensiGameId = canonicalGameId(contextData?.game || gameContext, 'pubg_mobile');
    const sensiMode = String(contextData?.mode || (file ? 'report_plus_video' : 'report_only')).toLowerCase();
    const sensiStatusActive = ['uploading', 'queued', 'processing'].includes(sensiStatus?.status);
    if (sensiProcessingRef.current || sensiStatusActive || !!sensiJobId) {
      console.info('⏭️ [SENSI-LOCK] Duplicate Sensi submission blocked. Existing processing is active.');
      setSensiStatus(prev => ({
        ...prev,
        subStatus: prev?.subStatus || t('app_sensi_duplicate_processing')
      }));
      return;
    }

    sensiProcessingRef.current = true;
    setIsProcessing(true);
    startLoading();

    if (sensiMode === 'report_only') {
      setSensiError(null);
      setSensiStall(false);
      setSensiStatus({ status: 'processing', progress: 45, subStatus: t('app_sensi_reading_report') });
      try {
        const token = await auth.currentUser.getIdToken();
        const diagnosticPayload = {
          ...(contextData?.diagnosticPayload || {}),
          schema_version: 'sensi_diagnostic_v1',
          mode: 'report_only',
          game_id: sensiGameId,
          video: { provided: false, metrics: null }
        };
        const response = await fetch(`${API}/sensi/diagnose`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(diagnosticPayload)
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          const err = new Error(data?.message || data?.error?.message || data?.error || t('app_sensi_diagnose_failed'));
          err.code = data?.error || data?.code || 'SENSI_DIAGNOSE_FAILED';
          err.status = response.status;
          throw err;
        }
        const normalizedSensiResult = normalizeAnalysis({
          id: data?.session_id || '',
          type: 'sensi',
          game: sensiGameId,
          status: 'done',
          result: data,
          analysis_summary: data?.diagnosis?.summary || '',
          sourcePipeline: 'sensi_report_only_v1',
          createdAt: new Date().toISOString()
        }, {
          fallbackType: 'sensi',
          fallbackGame: sensiGameId,
          fallbackStatus: 'done',
          fallbackPipeline: 'sensi_report_only_v1'
        });
        setSensiStatus({
          status: 'done',
          progress: 100,
          subStatus: t('app_sensi_diagnosis_ready'),
          result: normalizedSensiResult,
          jobId: data?.session_id || '',
          error: null
        });
        setSensiError(null);
        setSensiStall(false);
        return { result: normalizedSensiResult, raw: data };
      } catch (error) {
        console.error('Sensi report-only diagnostic error:', error);
        const safeMessage = safeUiText(error?.message, t('app_sensi_report_failed'));
        setSensiError(safeMessage);
        setSensiStall(false);
        setSensiStatus({
          status: 'error',
          progress: 100,
          subStatus: safeMessage,
          error: error?.code || safeMessage,
          message: safeMessage
        });
        return null;
      } finally {
        sensiProcessingRef.current = false;
        setIsProcessing(false);
        stopLoading();
      }
    }
    
    if (!file) {
      sensiProcessingRef.current = false;
      setIsProcessing(false);
      stopLoading();
      const safeMessage = t('app_sensi_select_video_or_no_video');
      setSensiError(safeMessage);
      setSensiStatus({
        status: 'error',
        progress: 0,
        subStatus: safeMessage,
        error: 'SENSI_VIDEO_REQUIRED',
        message: safeMessage
      });
      return null;
    }

    const MAX_VIDEO_MB = 200;
    if (file && file.size > MAX_VIDEO_MB * 1024 * 1024) {
      sensiProcessingRef.current = false;
      setIsProcessing(false);
      stopLoading();
      setErrorModal({
        show: true,
        title: t('app_file_too_large_title'),
        message: t('app_sensi_video_limit_message', { maxMb: MAX_VIDEO_MB }),
        kind: 'upload_job'
      });
      return;
    }

    setSensiStatus({ status: 'uploading', progress: 5, subStatus: t('app_sensi_ballistic_start') });

    try {
      const token = await auth.currentUser.getIdToken();
      const accessResponse = await fetch(`${API}/sensi/access`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` }
      });
      const accessPayload = await accessResponse.json().catch(() => ({}));
      if (!accessResponse.ok || accessPayload?.allowed === false) {
        const accessError = new Error(
          accessPayload?.message ||
          t('sensi_free_limit_message')
        );
        accessError.code = accessPayload?.reason || accessPayload?.error || 'SENSI_ACCESS_BLOCKED';
        accessError.status = accessResponse.status;
        throw accessError;
      }

      const uploader = new ResilientUploader(file, {
        token,
        type: 'sensi',
        game: sensiGameId,
        language,
        context: JSON.stringify({
          ...contextData,
          game: sensiGameId
        }), // Pass weapon, settings and game context
        onProgress: (progress, subStatus) => {
          setSensiStatus(prev => ({ ...prev, status: 'uploading', progress, subStatus }));
          if (progress > 0) stopLoading();
        },
        onStatus: (subStatus) => {
          setSensiStatus(prev => ({ ...prev, subStatus }));
        },
        onError: (message, uploadError) => {
          sensiProcessingRef.current = false;
          setIsProcessing(false);
          stopLoading();
          const safeMessage = safeUiText(message, t('error_analysis_unavailable'));
          setSensiError(safeMessage);
          setSensiStall(false);
          setSensiStatus({
            status: 'error',
            progress: Number(uploadError?.status) === 403 ? 0 : 100,
            subStatus: safeMessage,
            error: uploadError?.code || safeMessage,
            message: safeMessage
          });
        },
        onComplete: (jobId) => {
          setSensiStatus({ status: 'queued', progress: 12, subStatus: t('app_sensi_video_received_validation') });
          setSensiJobId(jobId);
          localStorage.setItem(SENSI_JOB_STORAGE_KEY, jobId);
          localStorage.setItem(SENSI_JOB_TS_STORAGE_KEY, String(Date.now()));
          localStorage.setItem(SENSI_JOB_UID_STORAGE_KEY, auth.currentUser?.uid || '');
        }
      });

      await uploader.start();
    } catch (error) {
      console.error('Sensi Analysis error:', error);
      const rawErrorMessage = safeUiText(error?.message, '');
      const isAccessBlocked = error?.code === 'FREE_LIMIT_REACHED' || error?.code === 'SENSI_ACCESS_BLOCKED' || error?.status === 403;
      const safeMessage = isAccessBlocked
        ? safeUiText(rawErrorMessage, t('sensi_free_limit_message'))
        : safeUiText(rawErrorMessage, t('error_analysis_unavailable'));
      setSensiError(safeMessage);
      setSensiStall(!isAccessBlocked);
      setSensiStatus({
        status: 'error',
        progress: isAccessBlocked ? 0 : 100,
        subStatus: safeMessage,
        error: error?.code || safeMessage,
        message: safeMessage
      });
      sensiProcessingRef.current = false;
      setIsProcessing(false);
      stopLoading();
    }
  };

  const handleSensiDiagnosticFeedback = async (sessionId, feedbackPayload = {}) => {
    const normalizedSessionId = String(sessionId || '').trim();
    if (!normalizedSessionId) {
      throw new Error(t('sensi_feedback_missing_session'));
    }
    if (!auth.currentUser) {
      throw new Error(t('sensi_feedback_auth_required'));
    }
    const token = await auth.currentUser.getIdToken();
    const response = await fetch(`${API}/sensi/diagnose/${encodeURIComponent(normalizedSessionId)}/feedback`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        ...feedbackPayload,
        language,
        locale: language
      })
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const err = new Error(data?.message || data?.detail?.message || data?.error?.message || data?.error || t('sensi_feedback_save_failed'));
      err.code = data?.error || data?.code || data?.detail?.error || 'SENSI_FEEDBACK_FAILED';
      err.status = response.status;
      throw err;
    }
    return data;
  };

  const handleSensiDiagnosticRefinement = async (sessionId) => {
    const normalizedSessionId = String(sessionId || '').trim();
    if (!normalizedSessionId) {
      throw new Error(t('sensi_refine_missing_session'));
    }
    if (!auth.currentUser) {
      throw new Error(t('sensi_refine_auth_required'));
    }
    const token = await auth.currentUser.getIdToken();
    const response = await fetch(`${API}/sensi/diagnose/${encodeURIComponent(normalizedSessionId)}/refine`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        language,
        locale: language
      })
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const err = new Error(data?.message || data?.detail?.message || data?.error?.message || data?.error || t('sensi_refine_failed'));
      err.code = data?.error || data?.code || data?.detail?.error || 'SENSI_REFINE_FAILED';
      err.status = response.status;
      throw err;
    }
    const normalizedSensiResult = normalizeAnalysis({
      id: data?.refinement_id || data?.session_id || normalizedSessionId,
      type: 'sensi',
      game: data?.game_id || gameContext,
      status: 'done',
      result: data,
      analysis_summary: data?.diagnosis?.summary || '',
      sourcePipeline: 'sensi_report_refinement_v1',
      createdAt: new Date().toISOString()
    }, {
      fallbackType: 'sensi',
      fallbackGame: data?.game_id || gameContext,
      fallbackStatus: 'done',
      fallbackPipeline: 'sensi_report_refinement_v1'
    });
    setSensiStatus({
      status: 'done',
      progress: 100,
      subStatus: t('app_sensi_refinement_ready'),
      result: normalizedSensiResult,
      jobId: data?.refinement_id || normalizedSessionId,
      error: null
    });
    return { result: normalizedSensiResult, raw: data };
  };

  const resetCoachAnalysisView = useCallback(() => {
    setCurrentAnalysis(null);
    setSelectedVideo(null);
    setVideoPreview(null);
    setTrainingContext(null);
    setIsProcessing(false);
    setCoachStatus({ status: 'idle', progress: 0, subStatus: '', error: null });
    setCoachJobId(null);
    clearCoachJobStorage();
  }, [clearCoachJobStorage]);

  const handleSensiBack = useCallback(() => {
    const isSensiBusy = ['uploading', 'queued', 'processing'].includes(String(sensiStatus?.status || '').toLowerCase()) || Boolean(sensiJobId);
    if (!isSensiBusy) {
      setSensiStatus({ status: 'idle', progress: 0, subStatus: '', error: null });
      setSensiError(null);
      setSensiStall(false);
      clearSensiJobStorage();
    }
    setActiveTab('hub');
  }, [sensiStatus?.status, sensiJobId, clearSensiJobStorage, setActiveTab]);
  const { isChatOpen: mirroredIsChatOpen, syncMiState, setHandleChatRef } = useMi();

  // ── THE BRIDGE: Awareness & State Sync ──
  // 1. Sync INWARD (Shell -> App)
  useEffect(() => {
    if (mirroredIsChatOpen !== isChatOpen) {
      setIsChatOpen(mirroredIsChatOpen);
    }
  }, [mirroredIsChatOpen, isChatOpen]);

  // 2. Sync OUTWARD (App -> Shell) - THE AWARENESS BRIDGE
  useEffect(() => {
    syncMiState({
      mi,
      chatMessages,
      setChatMessages,
      credits: effectiveProfile?.credits || 0,
      activeTab,
      setActiveTab,
      chatInput,
      setChatInput,
      isAuthenticated: Boolean(user),
      isOnboardingActive: Boolean(showOnboarding),
      isProcessing,
      coachStatus,
      sensiStatus,
      hasPrivilegedAccess
    });
  }, [
    activeTab,
    chatInput,
    isProcessing,
    mi,
    chatMessages,
    effectiveProfile?.credits,
    coachStatus,
    sensiStatus,
    hasPrivilegedAccess,
    syncMiState,
    setActiveTab,
    setChatInput,
    setChatMessages,
    user,
    showOnboarding
  ]);

  const handleMiResponse = useCallback((response) => {
    if (!response) return;

    // 1. Extract backend fields (with safe fallbacks)
    const action = response.action || { type: 'none' };
    const mode = response.mode || 'TALK';
    const message = response.message || response.text;

    // 2. Map Mode to Standard Visual States
    let nextMood = 'idle';
    if (mode === 'ACT') nextMood = 'success';
    else if (mode === 'GUIDE') nextMood = 'explain';
    else if (mode === 'warning') nextMood = 'warning';

    // 3. Phase 2 & 3: Global State Reactivity
    setMi(prev => ({
      ...prev,
      mood: nextMood,
      message: message || prev.message
    }));

    // 4. Game Context Switch (Explicit Confirmation)
    const nextCanonicalGame = response.game ? canonicalGameId(response.game, '') : '';
    const nextLegacyGame = nextCanonicalGame ? toLegacyGameContext(nextCanonicalGame) : '';
    if (nextCanonicalGame && nextLegacyGame !== toLegacyGameContext(canonicalGameId(gameContext))) {
      const gameNames = {
        free_fire: 'Free Fire',
        pubg_mobile: 'PUBG Mobile',
        cod_mobile: 'COD Mobile'
      };
      const gameName = gameNames[nextCanonicalGame] || nextCanonicalGame;

      // Update Mi's presence with confirmation
      setMi(prev => ({
        ...prev,
        mood: 'success',
        message: `Entendi, você está jogando ${gameName} agora. Ajustando...`
      }));

      // Small delay for immersion before actual context switch
      setTimeout(() => {
        setGameContext(nextLegacyGame);
        setChatMessages([]); // D. CHAT + GAME CONTEXT SYNC: Clear history on game switch
        localStorage.setItem('mi_game_context', nextCanonicalGame);
        if (user?.uid) {
          updateDoc(doc(db, 'users', user.uid), {
            mainGame: nextCanonicalGame,
            main_game: nextLegacyGame
          }).catch((err) => {
            console.warn('[GAME-BINDING] Failed to persist game switch:', err?.message || err);
          });
        }
      }, 700);
    }

    // 5. Phase 1 & 4: Action Execution
    if (action.type === 'navigate' && action.target) {
      const validTabs = ['home', 'hub', 'coach', 'sensi', 'actions', 'training', 'settings', 'profile', 'pricing', 'history', 'more', 'calculator', 'freefire-calculator', 'mi-analysis'];
      if (validTabs.includes(action.target)) {
        setActiveTab(action.target);
        // Automated follow-up removed to prevent cost explosion
      }
    }
  }, [gameContext, setMi, setGameContext, setChatMessages, setActiveTab, user?.uid]);


  const handleChat = useCallback(async (directMessage, options = {}) => {
    const msg = directMessage || chatInput.trim();
    if (!msg || isChatSending) return;

    // Hard rule: backend chat calls are allowed only on explicit user send actions.
    if (options?.source !== 'user_send') {
      console.info('⏭️ [MI-CHAT-GUARD] Non-user chat trigger blocked.', {
        source: options?.source || 'unknown',
        preview: msg.slice(0, 40)
      });
      return;
    }

    // Hard block automated system events: never call backend for these messages.
    const isSystemEvent = msg.startsWith('[SYSTEM_EVENT:');
    if (isSystemEvent) {
      console.info('⏭️ [MI-SYSTEM] System event blocked from /chat:', msg);
      return;
    }

    if (!options.silent) {
      setChatInput('');
      const newMessages = [...chatMessages, { role: 'user', text: msg }];
      setChatMessages(newMessages);
    }

    setIsChatSending(true);

    try {
      const token = await auth.currentUser.getIdToken();

      // 📤 DEBUG: Log outgoing request
      console.log('📤 CHAT REQUEST:', { message: msg, history: chatMessages.slice(-10).map(m => ({ role: m.role, text: m.text })), language, lang });

      // Send message with history for context
      const response = await fetch(`${API}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          message: msg,
          history: chatMessages.slice(-10).map(m => ({ role: m.role, text: m.text })),
          language,
          locale: language,
          currentScreen: activeTab,
          current_game: toLegacyGameContext(gameContext),
          target: options.target
        })
      });

      const data = await response.json();

      // 📥 DEBUG: Log raw response
      console.log('📥 CHAT RESPONSE:', data);

      if (!response.ok) {
        // Handle specific or generic errors
        handleMiResponse({ mode: 'warning', message: data.message || t('chat_network_error') });

        const errorType = data.error;
        const errorMessage = data.message;

        if (response.status === 402 || errorType === 'NOT_ENOUGH_CREDITS') {
          // Chat is FREE — this shouldn't happen. Show generic retry.
          setChatMessages(prev => [...prev, {
            role: 'model',
            text: t('chat_temporary_issue')
          }]);
        } else {
          setChatMessages(prev => [...prev, {
            role: 'model',
            text: `**Mi:** ${errorMessage || t('chat_connection_retry')}`
          }]);
        }
      } else {
        // SUCCESS: Add AI response to state with action metadata
        const aiResponse = data.message ?? data.text ?? '';
        const miAction = data.action ?? null;
        const miMode = data.mode ?? 'TALK';

        // 🎯 DEBUG: Log extracted fields
        console.log('🎯 ACTION:', miAction);
        console.log('🧠 MODE:', miMode);
        console.log('🧩 INTENT:', data.intent);

        setChatMessages(prev => [...prev, {
          role: 'model',
          text: aiResponse,
          action: miAction,
          mode: miMode,
          intent: data.intent
        }]);

        // ── MI ENGINE INTEGRATION (SAFE ACTIONS) ──
        handleMiResponse(data);
      }
    } catch (error) {
      console.error('Chat error:', error);
      handleMiResponse({ mode: 'warning', message: t('chat_connection_error_short') });
      setChatMessages(prev => [...prev, {
        role: 'model',
        text: t('chat_connection_error_markdown')
      }]);
    } finally {
      setIsChatSending(false);
    }
  }, [chatInput, isChatSending, chatMessages, language, lang, activeTab, gameContext, handleMiResponse, setChatMessages, setIsChatSending, t]);

  // 3. Sync DISPATCHER (Expose handleChat to Shell)
  useEffect(() => {
    setHandleChatRef({ current: handleChat });
  }, [handleChat, setHandleChatRef]);

  const handleBuyCredits = async (amount) => {
    if (hasPrivilegedAccess) {
      setShowPaymentModal(false);
      setActiveTab('hub');
      alert(t('app_privileged_no_charge'));
      return;
    }
    try {
      setPaymentError('');
      startLoading();
      const result = await callApi(`${API}/create-checkout-session`, { amount });
      window.location.href = result.url;
    } catch (error) {
      console.error(error);
      setPaymentError(checkoutErrorMessage(error));
      setPaymentModalTab('packages');
      setShowPaymentModal(true);
    } finally {
      stopLoading();
    }
  };

  const handleSubscribe = async (planId) => {
    if (hasPrivilegedAccess) {
      setShowPaymentModal(false);
      setActiveTab('hub');
      alert(t('app_privileged_no_subscription'));
      return;
    }
    try {
      setPaymentError('');
      startLoading();
      const refCode = localStorage.getItem('referral_code') || '';
      const productCode = planId === "pro" ? "pro_monthly" : planId;
      const result = await createBillingCheckout(productCode, refCode);
      window.location.href = result.url;
    } catch (error) {
      console.error(error);
      setPaymentError(checkoutErrorMessage(error));
      setPaymentModalTab('plans');
      setShowPaymentModal(true);
    } finally {
      stopLoading();
    }
  };

  const handleBuyCalculatorLifetime = async () => {
    if (hasPrivilegedAccess) {
      setShowPaymentModal(false);
      setActiveTab('calculator');
      alert(t('app_privileged_calculator_unlocked'));
      return;
    }
    try {
      setPaymentError('');
      startLoading();
      const refCode = localStorage.getItem('referral_code') || '';
      const result = await createBillingCheckout("pubg_lifetime", refCode);
      window.location.href = result.url;
    } catch (error) {
      console.error(error);
      setPaymentError(checkoutErrorMessage(error));
      setPaymentModalTab('packages');
      setShowPaymentModal(true);
    } finally {
      stopLoading();
    }
  };

  const handleBuyFreefireCalculatorLifetime = async () => {
    if (hasPrivilegedAccess) {
      setShowPaymentModal(false);
      setActiveTab('freefire-calculator');
      alert(t('app_privileged_calculator_unlocked'));
      return;
    }
    try {
      setPaymentError('');
      startLoading();
      const refCode = localStorage.getItem('referral_code') || '';
      const result = await createBillingCheckout("ff_lifetime", refCode);
      window.location.href = result.url;
    } catch (error) {
      console.error(error);
      setPaymentError(checkoutErrorMessage(error));
      setPaymentModalTab('packages');
      setShowPaymentModal(true);
    } finally {
      stopLoading();
    }
  };




  const openPricing = (tab = 'packages') => {
    if (hasPrivilegedAccess) {
      alert(t('app_privileged_no_purchase'));
      return;
    }
    setPaymentError('');
    setPaymentModalTab(tab);
    setShowPaymentModal(true);
  };
  // ── MI BEHAVIOR ENGINE: Centralized Reactions ──
  useEffect(() => {
    // Phase 4: Context Awareness Engine (LOCAL ONLY)
    let nextMood = 'idle';
    let nextContext = 'MI OS';
    let localMsg = mi.message;

    // Navigation Context Mapping + Local Messages
    const navData = {
      home: { ctx: t('app_mi_context_hub'), msg: t('app_mi_msg_hub') },
      hub: { ctx: t('app_mi_context_hub'), msg: t('app_mi_msg_hub') },
      actions: { ctx: t('app_mi_context_tools'), msg: t('app_mi_msg_tools') },
      training: { ctx: t('app_mi_context_training'), msg: t('app_mi_msg_training') },
      coach: { ctx: t('app_mi_context_coach'), msg: t('app_mi_msg_coach') },
      sensi: { ctx: t('app_mi_context_sensi'), msg: t('app_mi_msg_sensi') },
      profile: { ctx: t('app_mi_context_profile'), msg: t('app_mi_msg_profile') }
    };

    const currentNav = navData[activeTab] || { ctx: t('app_mi_context_os'), msg: mi.message };

    // Status mapping (Highest priority first)
    if (errorModal?.show) {
      nextMood = 'warning';
      nextContext = t('app_mi_context_alert');
      localMsg = t('app_critical_error_detected');
    } else if (coachStatus.status === 'processing' || sensiStatus.status === 'processing' || isChatSending) {
      nextMood = 'thinking';
      nextContext = t('app_mi_context_processing');
    } else if (coachStatus.status === 'uploading' || sensiStatus.status === 'uploading') {
      nextMood = 'thinking';
      nextContext = t('app_mi_context_syncing');
    } else if (coachStatus.status === 'done' || sensiStatus.status === 'done') {
      nextMood = 'success';
      nextContext = t('app_mi_context_analyzed');
    } else {
      // Idle/Navigation state - Local reaction
      nextContext = currentNav.ctx;
      // Only set local message if it hasn't been set by an AI response (heuristic)
      if (!isChatSending && coachStatus.status === 'idle' && sensiStatus.status === 'idle') {
          localMsg = currentNav.msg;
      }
    }

    if (!user) localMsg = t('app_mi_waiting_for_you');

    setMi(prev => ({
      ...prev,
      mood: nextMood,
      context: nextContext,
      message: localMsg
    }));
  }, [user, activeTab, isChatSending, coachStatus.status, sensiStatus.status, errorModal, mi.message, setMi, t]);

  // IDLE DETECTION REMOVED to prevent automated AI calls
  /*
  useEffect(() => {
    if (!user) return;
    ...
  }, [user, activeTab, handleChat]);
  */

  const handleAcceptTerms = useCallback(async (payload = {}) => {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error(t('app_user_not_authenticated'));

    const requestedTypes = Array.isArray(payload?.types) && payload.types.length > 0
      ? payload.types
      : ['user_terms', 'privacy_policy'];

    const token = await currentUser.getIdToken();
    const acceptResponse = await fetch(`${API}/legal/accept`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ types: requestedTypes })
    });

    if (!acceptResponse.ok) {
      let detail = {};
      try { detail = await acceptResponse.json(); } catch { detail = {}; }
      const message =
        detail?.detail?.message ||
        detail?.message ||
        detail?.error ||
        t('app_terms_acceptance_failed', { status: acceptResponse.status });
      throw new Error(message);
    }

    await setDoc(doc(db, 'users', currentUser.uid), {
      termsAccepted: true,
      termsAcceptedAt: new Date().toISOString(),
      termsAcceptedSource: 'frontend_gate'
    }, { merge: true });

    setTermsAccepted(true);
    writeCachedTermsAccepted(currentUser.uid, true);
  }, [t]);



  const currentPathname = (typeof window !== 'undefined' ? window.location.pathname : '/') || '/';
  const normalizedPathname = currentPathname.length > 1 ? currentPathname.replace(/\/+$/, '') : currentPathname;
  const isSecurityRoute = ['/seguranca', '/security', '/seguridad'].includes(normalizedPathname);
  const publicRoute = getPublicRouteConfig(normalizedPathname);
  const isLoginRoute = normalizedPathname === '/login';

  useEffect(() => {
    if (isLoginRoute) applyPublicSeoMeta(LOGIN_ROUTE_META, language);
  }, [isLoginRoute, language]);

  useEffect(() => {
    if (!user) return;

    document.documentElement.classList.remove('gph-public-page');
    document.body.classList.remove('gph-public-page');

    ['overflow-y', 'overflow-x', 'height'].forEach((property) => {
      document.documentElement.style.removeProperty(property);
      document.body.style.removeProperty(property);
    });

    ['#root', '.mi-root', '.mi-shell-content', '.app-main-layout', '.app-container'].forEach((selector) => {
      document.querySelectorAll(selector).forEach((element) => {
        element.style.removeProperty('overflow');
        element.style.removeProperty('height');
        element.style.removeProperty('min-height');
      });
    });

    if (isLoginRoute) {
      window.history.replaceState({}, '', '/');
    }
  }, [isLoginRoute, user]);

  if (publicRoute) {
    if (!authInitialized) {
      // Firebase auth is still initializing — don't show landing page yet.
      // Logged-in users would see a flash of the public page otherwise.
      return null;
    }
    if (publicRoute.kind === 'landing' && user) {
      // User is logged in, fall through to main dashboard
    } else {
      return publicRoute.kind === 'landing'
        ? <PublicLandingPage />
        : <PublicSeoPage route={publicRoute} />;
    }
  }

  if (isSecurityRoute) {
    return <SecurityPage />;
  }

  if (!user) {
    return (
      <div className="auth-wrapper h-[100dvh] w-full flex flex-col overflow-y-auto overflow-x-hidden">
        <AuthScreen
          mi={mi}
          setMiState={(mood) => setMi(prev => ({ ...prev, mood }))}
          onReferral={(code) => {
            setReferralCode(code);
            if (code) localStorage.setItem('referral_code', code);
          }}
        />
      </div>
    );
  }

  // Block unverified email users
  const signInProvider = user.providerData?.[0]?.providerId || '';
  if (signInProvider !== 'google.com' && !user.emailVerified) {
    return (
      <div className="auth-wrapper h-[100dvh] w-full flex flex-col overflow-y-auto overflow-x-hidden">
        <EmailVerificationGate user={user} />
      </div>
    );
  }

  if (termsAccepted === null) {
    return (
      <div
        className="auth-wrapper"
        style={{
          minHeight: '100dvh',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
          background: '#000',
          color: '#f5f7fa'
        }}
      >
        <div style={{ display: 'grid', gap: 10, textAlign: 'center' }}>
          <strong style={{ fontSize: 16, fontWeight: 700 }}>Entrando no Game Pro Hub</strong>
          <span style={{ fontSize: 13, color: 'rgba(245, 247, 250, 0.62)' }}>Sincronizando sua conta com a Mi...</span>
        </div>
      </div>
    );
  }

  // Show terms gate if not accepted
  if (termsAccepted !== true) {
    return (
      <div className="auth-wrapper h-[100dvh] w-full flex flex-col overflow-y-auto overflow-x-hidden">
        <TermsAcceptanceGate 
          onAccepted={handleAcceptTerms} 
          termsContent={localTermsContent}
        />
      </div>
    );
  }


  // ── RENDER CORE ──
  const hasAdminPanelAccess = user?.claims?.admin === true || profile?.role === 'admin';
  const isPartnerUser = profile?.role === 'partner';
  const referralInfo = isPartnerUser && profile?.referralCode
    ? {
      referralCode: profile.referralCode,
      referralCount: Number(profile.referralCount || 0),
      referralEarnings: Number(profile.referralEarnings || 0)
    }
    : null;
  const coachAnalyses = analyses.filter(isMiAnalysisRecord);
  const coachCurrentResult = currentAnalysis && isMiAnalysisRecord(currentAnalysis)
    ? currentAnalysis
    : null;
  const coachStatusResult = coachStatus?.result && isMiAnalysisRecord(coachStatus.result)
    ? coachStatus.result
    : null;
  const coachAnalysisResult = coachCurrentResult || coachStatusResult || null;
  const runtimeProfile = effectiveProfile || profile;

  // ── GPH SINGLE-SCREEN AGENT (UX primária) ──
  // A navegação por abas/navbar deixa de ser a experiência principal.
  // Fallback controlado: ?classic=1 restaura o shell antigo (rollback seguro).
  const useLegacyShell = typeof window !== 'undefined'
    && new URLSearchParams(window.location.search).has('classic');

  const gphHost = {
    user,
    profile: runtimeProfile,
    entitlements: normalizeEntitlements(runtimeProfile?.entitlements || {}),
    analyses,
    coachStatus,
    sensiStatus,
    isProcessing,
    gameContext,
    canonicalGame: canonicalGameId(runtimeProfile?.mainGame, 'pubg_mobile'),
    hasAdminPanelAccess,
    isPartnerUser,
    hasPrivilegedAccess,
    openPricing,
    openAdminPanel: () => setShowAdminPanel(true),
    openPartnerPanel: () => setShowPartnerPanel(true),
    signOut: () => signOut(auth),
    selectVideo: (file) => {
      if (!file) {
        setSelectedVideo(null);
        setVideoPreview(null);
        return;
      }
      setSelectedVideo(file);
      setVideoPreview(URL.createObjectURL(file));
    },
    selectedVideo,
    videoPreview,
    coachAnalysisResult,
    coachAnalyses,
    subscribe: handleSubscribe,
    startMiAnalysis: (file, context) => handleAnalyze(context, context?.game_id || 'pubg_mobile', { file, coachLaunchContext: context, analysisType: 'mi_analysis' }),
    startSensiAnalysis: handleSensiAnalyze,
    onSubmitDiagnosticFeedback: handleSensiDiagnosticFeedback,
    onRequestDiagnosticRefinement: handleSensiDiagnosticRefinement,
    // Telas existentes montadas pelo agente (engines intactas, sem reload).
    renderScreens: {
      sensi: ({ onBack }) => (
        <SensiOptimizer
          mi={mi}
          profile={runtimeProfile}
          gameContext={gameContext}
          isUploading={isProcessing}
          jobStatus={sensiStatus}
          onStartAnalysis={handleSensiAnalyze}
          onSubmitDiagnosticFeedback={handleSensiDiagnosticFeedback}
          onRequestDiagnosticRefinement={handleSensiDiagnosticRefinement}
          onOpenPricing={(tab = 'plans') => openPricing(tab)}
          lockToSensiOnly={true}
          onBack={onBack}
          error={sensiError}
          isStalled={sensiStall}
          onClearError={() => { setSensiError(null); setSensiStall(false); }}
        />
      ),
      miAnalysis: ({ onBack }) => (
        <MiAnalysisScreen
          user={user}
          profile={runtimeProfile}
          isProcessing={isProcessing}
          coachStatus={coachStatus}
          analysisResult={coachAnalysisResult}
          analyses={coachAnalyses}
          selectedVideo={selectedVideo}
          videoPreview={videoPreview}
          onSelectHistory={(item) => {
            if (item && !isMiAnalysisRecord(item)) return;
            setCurrentAnalysis(item);
            setVideoPreview(null);
          }}
          onSelectVideo={(file) => {
            if (!file) {
              setSelectedVideo(null);
              setVideoPreview(null);
              return;
            }
            if (file.size > 200 * 1024 * 1024) {
              alert(t('app_video_too_large'));
              return;
            }
            setSelectedVideo(file);
            setVideoPreview(URL.createObjectURL(file));
          }}
          onStartMentorshipAnalysis={(file, context) => handleAnalyze(context, context?.game_id || 'pubg_mobile', { file, coachLaunchContext: context, analysisType: 'mi_analysis' })}
          onOpenPricing={(tab = 'plans') => openPricing(tab)}
          onBack={() => {
            resetCoachAnalysisView();
            onBack();
          }}
          gameContext={gameContext}
        />
      ),
      calculatorPubg: () => (
        <PubgSensitivityCalculator
          profile={runtimeProfile}
          onOpenSensiAi={() => {}}
          onOpenPricing={(tab = 'plans') => openPricing(tab)}
        />
      ),
      calculatorFreefire: () => (
        <FreeFireSensitivityCalculator
          profile={runtimeProfile}
          onNavigate={() => {}}
          onOpenPricing={(tab = 'plans') => openPricing(tab)}
        />
      ),
      settings: ({ onBack }) => (
        <Settings
          mi={mi}
          user={user}
          profile={runtimeProfile}
          analyses={analyses}
          referralInfo={referralInfo}
          lang={lang}
          language={language}
          onOpenPricing={(tab = 'plans') => openPricing(tab)}
          onSignOut={() => signOut(auth)}
          onOpenAdmin={() => setShowAdminPanel(true)}
          onOpenPartner={() => setShowPartnerPanel(true)}
          onChangeLanguage={(newLang) => changeLanguage(newLang)}
          onGameContextChange={(nextCanonicalGame) => {
            const nextLegacyGame = toLegacyGameContext(canonicalGameId(nextCanonicalGame, 'pubg_mobile'));
            setGameContext(nextLegacyGame);
            setProfile(prev => prev ? { ...prev, mainGame: canonicalGameId(nextCanonicalGame, 'pubg_mobile'), main_game: nextLegacyGame } : prev);
          }}
          onBack={onBack}
        />
      )
    }
  };

  return (
    <div className="app-main-layout">

      <div className="app-container">



        <main className="flex-1 min-h-0 flex flex-col h-full overflow-hidden">
          <Suspense fallback={null}>
            {!useLegacyShell && <GphAgentApp host={gphHost} />}
            {useLegacyShell && (
            <SwipeNavigator activeTab={activeTab} setActiveTab={setActiveTab}>
              {activeTab === 'home' && (
                <GuidedHome
                  profile={runtimeProfile}
                  analyses={analyses}
                  onNavigate={setActiveTab}
                  onOpenPricing={(tab = 'plans') => openPricing(tab)}
                />
              )}

              {activeTab === 'coach' && (
                <MiCoachHome
                  key="hub-coach"
                  resetToHomeSignal={coachResetSignal}
                  initialView={initialCoachView}
                  onClearInitialView={() => setInitialCoachView(null)}
                  mi={mi}
                  user={user}
                  profile={runtimeProfile}
                  onOpenPricing={(tab = 'plans') => openPricing(tab)}
                />
              )}

              {activeTab === 'mi-analysis' && (
                <MiAnalysisScreen
                  user={user}
                  profile={runtimeProfile}
                  isProcessing={isProcessing}
                  coachStatus={coachStatus}
                  analysisResult={coachAnalysisResult}
                  analyses={coachAnalyses}
                  selectedVideo={selectedVideo}
                  videoPreview={videoPreview}
                  onSelectHistory={(item) => {
                    if (item && !isMiAnalysisRecord(item)) return;
                    setCurrentAnalysis(item);
                    setVideoPreview(null);
                  }}
                  onSelectVideo={(file) => {
                    if (!file) {
                      setSelectedVideo(null);
                      setVideoPreview(null);
                      return;
                    }
                    if (file.size > 200 * 1024 * 1024) {
                      alert(t('app_video_too_large'));
                      return;
                    }
                    setSelectedVideo(file);
                    setVideoPreview(URL.createObjectURL(file));
                  }}
                  onStartMentorshipAnalysis={(file, context) => handleAnalyze(context, context?.game_id || 'pubg_mobile', { file, coachLaunchContext: context, analysisType: 'mi_analysis' })}
                  onOpenPricing={(tab = 'plans') => openPricing(tab)}
                  onBack={() => {
                    resetCoachAnalysisView();
                    setActiveTab('actions');
                  }}
                  gameContext={gameContext}
                />
              )}



              {activeTab === 'history' && (
                <AnalysisHistory
                  analyses={analyses}
                  user={user}
                  profile={runtimeProfile}
                  onNavigate={(target) => {
                    if (typeof target === 'string') {
                      setActiveTab(target);
                      return;
                    }
                    if (target?.activeTab) {
                      setActiveTab(target.activeTab);
                    }
                  }}
                  onSelect={(item) => {
                    const itemType = String(item?.type || item?.analysisType || '').toLowerCase();
                    if (itemType === 'sensi') {
                      setActiveTab('sensi');
                      return;
                    }
                    setCurrentAnalysis(item);
                    setActiveTab('mi-analysis');
                  }}
                />
              )}

              {activeTab === 'training' && (
                <ProTrainingHub
                  key="training"
                  mi={mi}
                  userProfile={runtimeProfile}
                  analyses={analyses}
                  gameContext={gameContext}
                  onStartChat={(context) => {
                    setChatInput(context);
                    setActiveTab('chat');
                  }}
	                  trainingProgress={runtimeProfile?.trainingProgress || null}
                  drillContext={drillContext}
                  onClearDrillContext={() => setDrillContext(null)}
                  onNavigateToCoach={(ctx) => {
                    setTrainingContext(ctx);
                    setCurrentAnalysis(null);
                    setSelectedVideo(null);
                    setVideoPreview(null);
                    setActiveTab('coach');
                  }}
                  onOpenPricing={(tab = 'plans') => openPricing(tab)}
                />
              )}

              {activeTab === 'actions' && (
                <ArenaHub
                  profile={runtimeProfile}
                  onNavigate={setActiveTab}
                  onNavigateToMiAnalysis={navigateToMiAnalysis}
                  onOpenChat={() => setChatInput('')}
                  onOpenPricing={() => openPricing('packages')}
                  onGameContextChange={async (nextCanonicalGame) => {
                    const nextLegacyGame = toLegacyGameContext(canonicalGameId(nextCanonicalGame, 'pubg_mobile'));
                    setGameContext(nextLegacyGame);
                    localStorage.setItem('selected_game', nextCanonicalGame);
                    localStorage.setItem('gph_main_game', nextCanonicalGame);
                    setProfile(prev => prev ? { ...prev, mainGame: canonicalGameId(nextCanonicalGame, 'pubg_mobile'), main_game: nextLegacyGame } : prev);
                    if (user?.uid) {
                      try {
                        await updateDoc(doc(db, 'users', user.uid), {
                          mainGame: nextCanonicalGame,
                          main_game: nextLegacyGame,
                          updatedAt: new Date().toISOString()
                        });
                      } catch (err) {
                        console.warn('[GAME-CONTEXT-CHANGE] Failed to persist game context:', err);
                      }
                    }
                  }}
                />
              )}

              {activeTab === 'sensi' && (
                <SensiOptimizer
                  mi={mi}
                  profile={runtimeProfile}
                  gameContext={gameContext}
                  isUploading={isProcessing}
                  jobStatus={sensiStatus}
                  onStartAnalysis={handleSensiAnalyze}
                  onSubmitDiagnosticFeedback={handleSensiDiagnosticFeedback}
                  onRequestDiagnosticRefinement={handleSensiDiagnosticRefinement}
                  onOpenPricing={(tab = 'packages') => openPricing(tab)}
                  onBuyCalculatorLifetime={handleBuyCalculatorLifetime}
                  lockToSensiOnly={true}
                  onBack={handleSensiBack}
                  error={sensiError}
                  isStalled={sensiStall}
                  onClearError={() => { setSensiError(null); setSensiStall(false); }}
                />
              )}

              {activeTab === 'calculator' && (
                <div className="screen-shell screen-stack min-h-full">
                  <div className="screen-header mb-2">
                    <div>
                      <p className="screen-title">{t('app_pubg_calc_title')}</p>
                      <p className="screen-subtitle">{t('app_pubg_calc_subtitle')}</p>
                    </div>
                  </div>

                  <PubgSensitivityCalculator
                    profile={runtimeProfile}
                    onBuyCalculatorLifetime={handleBuyCalculatorLifetime}
                    onOpenSensiAi={() => setActiveTab('sensi')}
                    onOpenPricing={(tab = 'plans') => openPricing(tab)}
                  />
                </div>
              )}

              {activeTab === 'freefire-calculator' && (
                <div className="screen-shell screen-stack min-h-full">
                  <div className="screen-header mb-2">
                    <div>
                      <p className="screen-title">{t('app_freefire_calc_title')}</p>
                      <p className="screen-subtitle">{t('app_freefire_calc_subtitle')}</p>
                    </div>
                  </div>

                  <FreeFireSensitivityCalculator
                    profile={runtimeProfile}
                    onBuyFreefireCalculatorLifetime={handleBuyFreefireCalculatorLifetime}
                    onNavigate={setActiveTab}
                    onOpenPricing={(tab = 'plans') => openPricing(tab)}
                  />
                </div>
              )}

              {activeTab === 'profile' && (
                <Settings
                  mi={mi}
                  user={user}
                  profile={runtimeProfile}
                  analyses={analyses}
                  onOpenPricing={(tab = 'packages') => openPricing(tab)}
                  onSignOut={() => signOut(auth)}
                  onOpenAdmin={() => setShowAdminPanel(true)}
                  onOpenPartner={() => setShowPartnerPanel(true)}
                  referralInfo={referralInfo}
                  onGameContextChange={(nextCanonicalGame) => {
                    const nextLegacyGame = toLegacyGameContext(canonicalGameId(nextCanonicalGame, 'pubg_mobile'));
                    setGameContext(nextLegacyGame);
                    setProfile(prev => prev ? { ...prev, mainGame: canonicalGameId(nextCanonicalGame, 'pubg_mobile'), main_game: nextLegacyGame } : prev);
                  }}
                  onBack={() => setActiveTab('home')}
                />
              )}

              {activeTab === 'pricing' && (
                <Pricing
                  isPage={true}
                  onBuyCoins={handleBuyCredits}
                  onSubscribe={handleSubscribe}
                  onBuyCalculatorLifetime={handleBuyCalculatorLifetime}
                  onBuyFreefireCalculatorLifetime={handleBuyFreefireCalculatorLifetime}
                  paymentError={paymentError}
                  onClearPaymentError={() => setPaymentError('')}
                  currentPlan={(() => {
                    const plan = runtimeProfile?.subscriptionPlan || runtimeProfile?.proPlanKey || runtimeProfile?.planSource;
                    if (runtimeProfile?.proLifetime || runtimeProfile?.proAccessType === 'lifetime' || plan === 'pro_lifetime') return 'pro_lifetime';
                    const end = runtimeProfile?.currentPeriodEnd;
                    if (!plan || !end) return null;
                    return new Date(end) > new Date() ? plan : null;
                  })()}
                />
              )}

              {activeTab === 'more' && (
                <Settings
                  mi={mi}
                  user={user}
                  profile={runtimeProfile}
                  analyses={analyses}
                  referralInfo={referralInfo}
                  lang={lang}
                  language={language}
                  onOpenPricing={(tab = 'packages') => openPricing(tab)}
                  onSignOut={() => signOut(auth)}
                  onOpenAdmin={() => setShowAdminPanel(true)}
                  onOpenPartner={() => setShowPartnerPanel(true)}
                  onChangeLanguage={(newLang) => {
                    changeLanguage(newLang);
                  }}
                  onChangeGame={(nextCanonicalGame) => {
                    const nextLegacyGame = toLegacyGameContext(nextCanonicalGame);
                    setGameContext(nextLegacyGame);
                    setProfile(prev => prev ? { ...prev, mainGame: canonicalGameId(nextCanonicalGame, 'pubg_mobile'), main_game: nextLegacyGame } : prev);
                  }}
                  onGameContextChange={(nextCanonicalGame) => {
                    const nextLegacyGame = toLegacyGameContext(canonicalGameId(nextCanonicalGame, 'pubg_mobile'));
                    setGameContext(nextLegacyGame);
                    setProfile(prev => prev ? { ...prev, mainGame: canonicalGameId(nextCanonicalGame, 'pubg_mobile'), main_game: nextLegacyGame } : prev);
                  }}
                  onBack={() => setActiveTab('home')}
                />
              )}
            </SwipeNavigator>
            )}
          </Suspense>
        </main>

        <AnimatePresence>
          {showPaymentModal && (
            <Pricing
              onClose={() => {
                setShowPaymentModal(false);
                setPaymentError('');
              }}
              onBuyCoins={handleBuyCredits}
              onSubscribe={handleSubscribe}
              onBuyCalculatorLifetime={handleBuyCalculatorLifetime}
              onBuyFreefireCalculatorLifetime={handleBuyFreefireCalculatorLifetime}
              paymentError={paymentError}
              onClearPaymentError={() => setPaymentError('')}
              currentPlan={(() => {
                const plan = runtimeProfile?.subscriptionPlan || runtimeProfile?.proPlanKey || runtimeProfile?.planSource;
                if (runtimeProfile?.proLifetime || runtimeProfile?.proAccessType === 'lifetime' || plan === 'pro_lifetime') return 'pro_lifetime';
                const end = runtimeProfile?.currentPeriodEnd;
                if (!plan || !end) return null;
                return new Date(end) > new Date() ? plan : null;
              })()}
              initialTab={paymentModalTab}
            />
          )}

          {errorModal?.show && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="gph-global-modal-backdrop" style={{ zIndex: 'var(--z-overlays)' }}>
              <motion.div initial={{ scale: 0.94, y: 18 }} animate={{ scale: 1, y: 0 }} className="gph-global-modal-card">
                <div className="gph-global-modal-icon">
                  <ShieldAlert className="w-7 h-7" />
                </div>
                <h3>{safeUiText(errorModal.title, t('error_analysis_title'))}</h3>
                <p>{safeUiText(errorModal.message, t('app_premium_required'))}</p>
                <div className="gph-global-modal-actions">
                  {errorModal?.kind === 'premium_access' && !hasPrivilegedAccess ? (
                    <>
                      <button
                        onClick={() => {
                          setErrorModal({ show: false, title: '', message: '', kind: '' });
                          openPricing('packages');
                        }}
                        className="gph-global-modal-primary"
                      >
                        {t('app_view_plans')}
                      </button>
                      <button
                        onClick={() => setErrorModal({ show: false, title: '', message: '', kind: '' })}
                        className="gph-global-modal-secondary"
                      >
                        {t('error_close')}
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => setErrorModal({ show: false, title: '', message: '', kind: '' })}
                        className="gph-global-modal-primary"
                      >
                        {t('error_retry')}
                      </button>
                      <button
                        onClick={() => setErrorModal({ show: false, title: '', message: '', kind: '' })}
                        className="gph-global-modal-secondary"
                      >
                        {t('error_close')}
                      </button>
                    </>
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}

          {showAdminPanel && hasAdminPanelAccess && (
            <React.Suspense fallback={null}>
              <AdminPanel
                onClose={() => setShowAdminPanel(false)}
                userProfile={runtimeProfile}
              />
            </React.Suspense>
          )}

          {showPartnerPanel && runtimeProfile?.role === 'partner' && (
            <React.Suspense fallback={null}>
              <PartnerPanel
                onClose={() => setShowPartnerPanel(false)}
                user={user}
                profile={runtimeProfile}
                userProfile={runtimeProfile}
              />
            </React.Suspense>
          )}
        </AnimatePresence>


        {/* SwipeNavigator now includes the official bottom icon navigation. */}
        <InstallPWA />

	        <AnimatePresence>
	          {showOnboarding && user && (
	            <Suspense fallback={null}>
	              <OnboardingFlow
	                onComplete={(payload = {}) => {
                    const rawGameId = typeof payload === 'string' ? payload : payload.game;
	                  const canonicalMainGame = canonicalGameId(
	                    rawGameId || localStorage.getItem(ONBOARDING_GAME_KEY) || localStorage.getItem('selected_game') || 'pubg_mobile'
	                  );
	                  const legacyMainGame = toLegacyGameContext(canonicalMainGame);
                    const dayOneAction = typeof payload === 'object' ? String(payload.action || '') : '';
                    const nextTabAfterOnboarding = (() => {
                      if (dayOneAction === 'adjust_sensitivity') {
                        if (canonicalMainGame === 'free_fire') return 'freefire-calculator';
                        if (canonicalMainGame === 'pubg_mobile') return 'calculator';
                        return 'sensi';
                      }
                      if (dayOneAction === 'train_mechanics') return 'coach';
                      if (dayOneAction === 'analyze_gameplay') return 'mi-analysis';
                      return 'sensi';
                    })();
                  localStorage.setItem(ONBOARDING_DONE_KEY, '1');
                  localStorage.setItem(ONBOARDING_GAME_KEY, canonicalMainGame);
                  localStorage.setItem('gph_main_game', canonicalMainGame);
                  localStorage.setItem('mi_game_context', canonicalMainGame);
                  localStorage.setItem('selected_game', canonicalMainGame);
	                  setShowOnboarding(false);
	                  setGameContext(legacyMainGame);
	                  setProfile(prev => prev ? {
	                    ...prev,
	                    mainGame: canonicalMainGame,
	                    main_game: legacyMainGame,
	                    onboardingCompleted: true,
                      dayOneIntent: typeof payload === 'object' ? payload : null
	                  } : prev);
	                  if (user?.uid) {
	                    setDoc(
	                      doc(db, 'users', user.uid),
	                      {
	                        mainGame: canonicalMainGame,
	                        main_game: legacyMainGame,
	                        onboardingCompleted: true,
                          dayOneOnboarding: typeof payload === 'object' ? {
                            game: canonicalMainGame,
                            problem: payload.problem || '',
                            action: payload.action || '',
                            completedAt: new Date().toISOString()
                          } : null
	                      },
	                      { merge: true }
	                    ).catch((err) => {
	                      console.warn('[GAME-BINDING] Failed to persist onboarding completion:', err?.message || err);
	                    });
	                  }
                    setActiveTab(nextTabAfterOnboarding);
	                }}
	              />
	            </Suspense>
	          )}
	        </AnimatePresence>
      </div>
    </div>
  );
}

export default App;

